import { graphqlOperation } from "@aws-amplify/api";
import { DataStore } from "@aws-amplify/datastore";
import useAPI from "@toothfairy/shared-api/useApi";
import AppQuantumEditorUtils from "@toothfairy/shared-ui/AppQuantumEditorUtils";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import axios from "axios";
import dayjs from "dayjs";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "react-reactive-var";
import {
  chatSidebarCollapsed,
  isConnectedToCloud,
  isElectronRunning,
  isOllamaRunning,
  oLlamaModelLoaded,
} from "../../../App";
import envConfig from "../../../envConfig";
import { subscribeGraphQL } from "../../API";
import agentsHelper from "../../API/Agents";
import chatsHelper from "../../API/Chats/chatsHelper";
import Files from "../../API/Files";
import hostingsHelper from "../../API/Hostings/hostingsHelper";
import Live from "../../API/Live";
import Media from "../../API/Media";
import messagesHelper from "../../API/Messages/messagesHelper";
import functionsHelper from "../../API/Functions/functionsHelper";
import trainingAPI from "../../API/Training/trainingAPI";
import usersAPI from "../../API/Users/usersAPI";
import { onUpdateChatPlanner } from "../../graphql/custom";
import {
  onCreateChatMessageByChatIDEvent,
  onUpdateChatMessageByChatIDEvent,
} from "../../graphql/subscriptions";
import { applyCanvasOperations } from "../../Utils/canvasOperationsHandler";
import { handlePullFile, handlePushFile } from "../../Utils/localFileTransfer";
import { getPlannerUnifiedStatus } from "../../Utils/notificationUtils";
import {
  getCascadingDisables,
  getAgenticCycleState,
  getNextAgenticCycleState,
  AGENTIC_CYCLE_STATES,
} from "../../Utils/toggleableToolsConfig";
import { extractAllArtifactsFromMessages } from "../../Utils/plannerStepUtils";
import useOllamaModel from "../../Hooks/useOllamaModels";
import useRecording from "../../Hooks/useRecording";
import { useTextToSpeech } from "../../Hooks/useTextToSpeech";
import useAutoSave from "../../Hooks/useAutoSave";
import useAgentStream from "../../Hooks/useAgentStream";
import AgentStream from "../../API/AgentStream";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { Chat } from "../../models";
import { useHistory, useParams } from "../../Router";
import { isMobileScreen } from "../MainViewContainer";
import ChatsScreenView from "./ChatsScreenView";
import LiveVoiceProvider from "./Components/LiveVoiceContext";
import PlanViewerModal from "../../Components/PlanViewerModal";
import {
  extractTextFromFile,
  imageUrlsToBase64Array,
} from "../../Utility/localFileProcessor";
import { useHomeMode } from "../Home/HomeModeContext";

// Module-level store for pending messages that must survive component remounts
// (e.g., when navigating from /home to /chats after agent switch)
let _pendingMessage = null;

const maxWaitTime = 2000;

// ==============================================================================
// LOCAL FOLDER ACCESS - Execute tool requests from backend agent
// ==============================================================================

/**
 * Execute a local folder access operation via Electron IPC
 * @param {string} operation - The operation type: list, read, search, analyze, write
 * @param {object} params - Operation parameters
 * @param {string} boundFolderPath - The bound folder path for security validation
 * @returns {Promise<object>} - Operation result
 */
const executeLocalFolderOperation = async (
  operation,
  params,
  boundFolderPath,
) => {


  // Ensure we're in Electron environment
  if (!window?.electronAPI) {
    console.error("[LocalFolderAccess] Electron API not available:", {
      hasWindow: typeof window !== "undefined",
      hasElectronAPI: !!window?.electronAPI,
      availableAPIs: window?.electronAPI
        ? Object.keys(window.electronAPI)
        : "none",
    });
    return {
      success: false,
      error: "Local folder access is only available in the desktop application",
    };
  }

console.log("[LocalFolderAccess] Electron API available. Methods:", {
    hasListFiles: typeof window.electronAPI.listFiles === "function",
    hasReadFile: typeof window.electronAPI.readFile === "function",
    hasSearchFiles: typeof window.electronAPI.searchFiles === "function",
    hasAnalyzeFolder: typeof window.electronAPI.analyzeFolder === "function",
    hasWriteFile: typeof window.electronAPI.writeFile === "function",
    allMethods: Object.keys(window.electronAPI),
  });

  // Helper: Resolve relative path to absolute path
  const resolvePath = (relativePath) => {
    if (!relativePath || relativePath === "") {
      return boundFolderPath;
    }
    // If it's already an absolute path (starts with bound folder), return as-is
    if (relativePath.startsWith(boundFolderPath)) {
      return relativePath;
    }
    // Otherwise, join with bound folder path
    const separator =
      boundFolderPath.endsWith("/") || boundFolderPath.endsWith("\\")
        ? ""
        : "/";
    return boundFolderPath + separator + relativePath;
  };

  // Security: Validate path is within bound folder
  const validatePath = (targetPath) => {
    if (!targetPath || !boundFolderPath) {

      return false;
    }
    const normalizedTarget = targetPath.replace(/\\/g, "/").toLowerCase();
    const normalizedBound = boundFolderPath.replace(/\\/g, "/").toLowerCase();
    const isValid = normalizedTarget.startsWith(normalizedBound);

    return isValid;
  };

  try {
    let result;
    switch (operation) {
      case "list": {
        const { path: providedPath, recursive = false, maxDepth = 3 } = params;
        const absolutePath = resolvePath(providedPath);

        if (!validatePath(absolutePath)) {
console.error("[LocalFolderAccess] LIST path validation failed", {
            providedPath,
            absolutePath,
            boundFolderPath,
          });
          return {
            success: false,
            error: `Path ${providedPath} is outside the bound folder`,
          };
        }

        result = await window.electronAPI.listFiles(absolutePath, {
          recursive,
          maxDepth,
        });

        return result;
      }

      case "read": {
        const { path: relativePath } = params;
        const absolutePath = resolvePath(relativePath);

        if (!validatePath(absolutePath)) {
console.error("[LocalFolderAccess] READ path validation failed:", {
            relativePath,
            absolutePath,
            boundFolderPath,
          });
          return {
            success: false,
            error: `Path ${relativePath} is outside the bound folder`,
          };
        }

        result = await window.electronAPI.readFile(absolutePath, {
          maxSize: params.maxSize,
        });

        return result;
      }

      case "search": {
        const { pattern, path: providedSearchPath, contentPattern } = params;
        const absolutePath = resolvePath(providedSearchPath);

        if (!validatePath(absolutePath)) {
console.error("[LocalFolderAccess] SEARCH path validation failed:", {
            providedSearchPath,
            absolutePath,
            boundFolderPath,
          });
          return {
            success: false,
            error: `Path ${providedSearchPath} is outside the bound folder`,
          };
        }

        result = await window.electronAPI.searchFiles(absolutePath, pattern, {
          maxResults: params.maxResults || 100,
          includeContent: !!contentPattern,
          contentPattern,
        });

        return result;
      }

      case "analyze": {
        const { path: providedAnalyzePath } = params;
        const absolutePath = resolvePath(providedAnalyzePath);

        if (!validatePath(absolutePath)) {
console.error("[LocalFolderAccess] ANALYZE path validation failed:", {
            providedAnalyzePath,
            absolutePath,
            boundFolderPath,
          });
          return {
            success: false,
            error: `Path ${providedAnalyzePath} is outside the bound folder`,
          };
        }

        result = await window.electronAPI.analyzeFolder(absolutePath);

        return result;
      }

      case "write": {
        const { path: relativePath, content } = params;
        const absolutePath = resolvePath(relativePath);

        if (!validatePath(absolutePath)) {
console.error("[LocalFolderAccess] WRITE path validation failed:", {
            relativePath,
            absolutePath,
            boundFolderPath,
          });
          return {
            success: false,
            error: `Path ${relativePath} is outside the bound folder`,
          };
        }

        result = await window.electronAPI.writeFile(absolutePath, content, {
          createDir: true,
        });

        return result;
      }

      default:
console.error("[LocalFolderAccess] Unknown operation:", {
          operation,
          validOperations: ["list", "read", "search", "analyze", "write"],
        });
        return { success: false, error: `Unknown operation: ${operation}` };
    }
  } catch (error) {
console.error("[LocalFolderAccess] Operation error:", {
      operation,
      params,
      boundFolderPath,
      errorName: error?.name,
      errorMessage: error?.message,
      errorStack: error?.stack,
    });
    return { success: false, error: error.message || "Operation failed" };
  }
};

/**
 * Post tool result back to backend
 * @param {string} requestId - The tool request ID
 * @param {object} result - The operation result
 * @param {string} workspaceToken - Auth token
 */
const postToolResultToBackend = async (
  requestId,
  result,
  workspaceToken,
  workspaceid,
) => {


  try {
    const apiUrl = `${envConfig?.REACT_APP_AI_REST_ENDPOINT}/tool-result`;
    const payload = {
      request_id: requestId,
      result: result,
      timestamp: Date.now(),
      workspaceid: workspaceid,
    };



    const response = await axios.post(apiUrl, payload, {
      headers: {
        "Content-Type": "application/json",
        "x-api-key": workspaceToken,
      },
      timeout: 30000, // 30 second timeout
    });



    return { success: true };
  } catch (error) {
console.error("[LocalFolderAccess] Failed to post tool result:", {
      requestId,
      workspaceid,
      errorName: error?.name,
      errorMessage: error?.message,
      errorCode: error?.code,
      responseStatus: error?.response?.status,
      responseData: error?.response?.data,
      isAxiosError: error?.isAxiosError,
    });
    return { success: false, error: error.message };
  }
};

/**
 * Handle tool_request SSE event from backend
 * Executes local folder operation and posts result back
 */
const handleToolRequest = async (
  data,
  boundFolderPath,
  workspaceToken,
  workspaceid,
) => {
  const { request_id, tool_name, arguments: toolArgs } = data;

  console.log("=".repeat(80));



  // Only handle local_folder_access tool
  if (tool_name !== "local_folder_access") {

    return;
  }



  // Validate we have a bound folder
  if (!boundFolderPath) {
console.error("[LocalFolderAccess] No bound folder path!", {
      request_id,
      tool_name,
      operation: toolArgs?.operation,
      message: "Cannot execute operation - no folder bound to chat",
    });
    const errorResult = {
      success: false,
      error: "No folder is bound to this chat. Please bind a folder first.",
    };

    await postToolResultToBackend(
      request_id,
      errorResult,
      workspaceToken,
      workspaceid,
    );
    return;
  }



  // Execute the operation
  const operation = toolArgs?.operation || "list"; // Default to "list" if not provided


  const startTime = Date.now();
  const result = await executeLocalFolderOperation(
    operation,
    toolArgs,
    boundFolderPath,
  );
  const executionTime = Date.now() - startTime;



  // Post result back to backend

  const postResult = await postToolResultToBackend(
    request_id,
    result,
    workspaceToken,
    workspaceid,
  );


  console.log("=".repeat(80));
};
const Chats = ({ builderMode = false }) => {
  const { chatId } = useParams();
  const history = useHistory();
  const { mode } = AppTheme?.useTheme();
  const homeMode = useHomeMode();
  const _routeBase = homeMode?.routeBase || "chats";
  const cursor = mode === "light" ? "\u26AB" : "\u26AA";
  const [lastUpdateMessageTime, setLastUpdateMessageTime] = useState(null);
  const lastUpdateMessageTimeRef = useRef({});
  const {
    getActiveWorkspace,
    getWorkspaceEntities,
    generateAdaptedTopics,
    loadChats,
    getAgent,
    getChat,
    isChatHistoryLoaded,
    setIsChatHistoryLoaded,
    getPlanConfig,
    isUserAIEngineer,
    isUserContentAdmin,
    getUserName,
    getWorkspaceUsers,
  } = useWorkspaces();
  const {
    updateText: updateTextForSpeech,
    currentText,
    processedText,
    isSpeechGettingProcessed,
    stopAudio,
    isPlaying,
    setIsPlaying,
    listenToAudio,
  } = useTextToSpeech();
  const { generateSystemMessage } = useOllamaModel();
  const [lastSubscribedChatId, setLastSubscribedChatId] = useState(null);
  const isSendingEventRef = useRef(false);
  const [agentHosting, setAgentHosting] = useState(null);
  const [errorWithSubscription, setErrorWithSubscription] = useState(false);
  const [subscriptionObj, setSubscriptionObject] = useState(null);
  const [chatSubscriptionObj, setChatSubscriptionObject] = useState(null);
  const [chatMetadata, setChatMetadata] = useState(null);
  const subscriptionObjRef = useRef(null);
  const chatSubscriptionObjRef = useRef(null);
  const createSubscriptionRef = useRef(null);
  const [isSubscriptionHealthy, setIsSubscriptionHealthy] = useState(false);
  const [isChatSubscriptionHealthy, setIsChatSubscriptionHealthy] = useState(
    false,
  );
  const [
    isCreateSubscriptionHealthy,
    setIsCreateSubscriptionHealthy,
  ] = useState(false);
  const [allowStop, setAllowStop] = useState(false);
  const [selectedImageKey, setSelectedImageKey] = useState(null);
  const [selectedImageRawKey, setSelectedImageRawKey] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const lastMessageCreatedRef = useRef(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileKey, setFileKey] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioKey, setAudioKey] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoKey, setVideoKey] = useState(null);
  const [fileUrls, setFileUrls] = useState(null);
  const [fileKeys, setFileKeys] = useState(null);
  const [audioUrls, setAudioUrls] = useState(null);
  const [imageUrls, setImageUrls] = useState(null);
  const [audioKeys, setAudioKeys] = useState(null);
  const [imageKeys, setImageKeys] = useState(null);
  const [canvasSelection, setCanvasSelection] = useState(null);
  const [boundFolderPath, setBoundFolderPath] = useState(null);
  const chatIdRef = useRef(null);
  const chatOpenedAtRef = useRef(null);
  const boundFolderPathRef = useRef(null);
  const _pullPushInProgressRef = useRef(new Set());
  const [isChatReady, setIsChatReady] = useState(false);
  const [isSubscriptionRequired, setIsSubscriptionRequired] = useState(false);
  const __isAIStreamingForSubscription = useRef(false);
  const streamingMessageIDRef = useRef(null);
  const [audiosText, setAudiosText] = useState("");
  const [videosText, setVideosText] = useState("");
  const [pendingSendText, setPendingSendText] = useState(null);
  const [planningExecution, setPlanningExecution] = useState(null);
  const [audioPlayBackMessage, setAudioPlayBackMessage] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  // Track multiple active steps for parallel execution visualization
  const [activeSteps, setActiveSteps] = useState(new Set());
  const [totalSteps, setTotalSteps] = useState(0);
  const [streamingMessageID, setStreamingMessageID] = useState(null);
  const [loadedMessages, setLoadedMessages] = useState(8);
  const [stateMessages, setStateMessages] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [planArtifacts, setPlanArtifacts] = useState([]);
  const [stepsStatusFromArtifacts, setStepsStatusFromArtifacts] = useState([]);
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [selectedSources, setSelectedSources] = useState([]);
  const [showWidgetChatsHistory, setShowWidgetChatsHistory] = useState(false);
  const [chatObject, setChatObject] = useState(null);
  const [primaryAgent, setPrimaryAgent] = useState(null);
  const [liveSessionId, setLiveSessionId] = useState(null);
  const liveSessionChatRef = useRef(null);
  const primaryAgentModeRef = useRef(null);
  const MAX_MESSAGES_LOADED = primaryAgent?.mode === "planner" ? 50 : 8;
  const [isLoading, setIsLoading] = useState(true);
  const [loadedChatId, setLoadedChatId] = useState(null);
  const [_coreTreeViewData, setCoreTreeViewData] = useState(null);
  const [inputText, setInputText] = useState("");
  const [showModal, setShowModal] = React.useState(false);
  const [fixedDocumentId, setFixedDocumentId] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [hasAccessToChat, setHasAccessToChat] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [canInteractWithChat, setCanInteractWithChat] = useState(true);
  const [
    creationOfNewChatInProgress,
    setCreationOfNewChatInProgress,
  ] = React.useState(false);
  const [deletionInProgress, setDeletionInProgress] = React.useState(false);
  const isCollapsed = useReactiveVar(chatSidebarCollapsed);
  const setIsCollapsed = (value) => chatSidebarCollapsed(value);
  const [treeViewActionContext, setTreeViewActionContext] = useState("");
  // Plan mode state
  const [planMode, setPlanMode] = useState(false);
  const [planFile, setPlanFile] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  // Model change state
  const [isModelChangeInProgress, setIsModelChangeInProgress] = useState(false);
  // Tool toggle state - tracks which property is currently being saved
  const [toolToggleInProgress, setToolToggleInProgress] = useState(null);
  // Queue for pending tool toggles
  const pendingToolTogglesRef = useRef([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planContent, setPlanContent] = useState(null);
  const [planContentLoading, setPlanContentLoading] = useState(false);
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const { user } = AppUser.useUser();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const lastChangedChatRef = useRef(null);
  const loadingMessageIdRef = useRef(null);
  const editorRef = useRef(null);
  const pendingCanvasOpsRef = useRef(null);
  const canvasCreationInProgressRef = useRef(false);

  const [imageUrl, setImageUrl] = useState(null);
  const [imageKey, setImageKey] = useState(null);
  const chats = state?.chats;
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef(messages);
  const localStreamingController = useRef(null);
  const [isAIStreaming, setIsAIStreaming] = useState(false);
  const isAIStreamingRef = useRef(false);
  const [waitingForFirstResponse, setWaitingForFirstResponse] = useState(false);
  const [waitingForFirstChunk, setWaitingForFirstChunk] = useState(false);
  const [isAwaitingUserInput, setIsAwaitingUserInput] = useState(false);
  // Track agent processing status from SSE events
  const [currentProcessingStatus, setCurrentProcessingStatus] = useState(null);
  const currentProcessingStatusRef = useRef(null);
  // Workspace functions for resolving agent function IDs
  const [workspaceFunctions, setWorkspaceFunctions] = useState([]);
  const workspaceFunctionsLoadedRef = useRef(null); // Track which workspace functions were loaded for
  const setChats = (chats) => {
    dispatch("SET_CHATS", chats);
  };

  /**
   * Handle quota/rate limit errors from SSE streaming
   * Shows appropriate toast message based on error type
   * @param {Error} error - The error object from SSE stream
   * @returns {boolean} - True if error was a quota error (handled), false otherwise
   */
  const handleQuotaError = useCallback(
    (error) => {
      if (!error?.isQuotaError) {
        return false;
      }

      const { QUOTA_ERROR_TYPES } = AgentStream;

      if (error.quotaType === QUOTA_ERROR_TYPES.MONTHLY_LIMIT) {
        AppToasty.show(error.errorMessage || t("common.quotaLimitReached"), {
          placement: "bottom",
          type: "warning",
        });
      } else if (error.quotaType === QUOTA_ERROR_TYPES.RATE_LIMIT) {
        AppToasty.show(error.errorMessage || t("common.rateLimitExceeded"), {
          placement: "bottom",
          type: "warning",
        });
      } else {
        // Generic quota error
        AppToasty.show(error.errorMessage || t("common.quotaLimitReached"), {
          placement: "bottom",
          type: "warning",
        });
      }

      return true;
    },
    [AppToasty, t],
  );

  /**
   * Check if we're in post-task cleanup phase (generating tables, mermaid charts, summaries, etc.)
   * @returns {boolean} - True if agent_processing_status starts with 'post_task_'
   */
  const isPostTaskCleanupPhase = useCallback(() => {
    const status = currentProcessingStatusRef?.current;
    if (!status) return false;
    return status?.startsWith("post_task_");
  }, []);

  const topics = getWorkspaceEntities("topic")?.map((topic) => {
    return {
      id: topic?.id,
      name: topic?.label,
      description: topic?.description,
      type: topic?.type,
      category: topic.type,
    };
  });
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodeToDelete, setSelectedNodeToDelete] = useState(null);
  useEffect(() => {
    // Cancel any ongoing SSE stream when switching chats
    if (isSSEStreaming) {
      cancelSSEStream();
    }
    setDocumentId(null);
    canvasCreationInProgressRef.current = false;
    setPlanMode(false);
    setChatObject(null);
    setImageUrl(null);
    setImageUrls(null);
    setImageKeys(null);
    setAudioUrls(null);
    setAudioKeys(null);
    setImageKey(null);
    setAudioUrl(null);
    setFileKey(null);
    setFileUrl(null);
    setFileKeys(null);
    setFileUrls(null);
    setAudioKey(null);
    setVideoUrl(null);
    setVideoKey(null);
    setSelectedImageKey(null);
    setIsAIStreaming(false);
    setWaitingForFirstResponse(false);
    setWaitingForFirstChunk(false);
    setIsAwaitingUserInput(false);
    setLastUpdateMessageTime(null);
    lastChangedChatRef.current = null;
    loadingMessageIdRef.current = null;
    setActiveStep(null);
    setActiveSteps(new Set()); // Reset parallel steps tracking
    isSendingEventRef.current = false;
    chatIdRef.current = chatId;
    chatOpenedAtRef.current = Date.now();
    setIsChatReady(false);
    setStreamingMessageID(null);
    // Reset plan mode state

    setPlanFile(null);
    setPlanStatus(null);
    setPlanContent(null);
  }, [chatId]);
  useEffect(() => {
    primaryAgentModeRef.current = primaryAgent?.mode;
  }, [primaryAgent?.mode]);
  useEffect(() => {
    const maxMessages = primaryAgent?.mode === "planner" ? 50 : 8;
    setLoadedMessages(maxMessages);
  }, [primaryAgent?.mode]);
  const { transcript, startRecording, stopRecording } = useRecording({
    onForcedStop: () => {},
  });
  const {
    startStream: startSSEStream,
    cancelStream: cancelSSEStream,
    isStreaming: isSSEStreaming,
    connectionStatus: sseConnectionStatus,
    getAccumulatedText,
    resetAccumulatedText,
  } = useAgentStream();
  const isSSEStreamingRef = useRef(false);
  useEffect(() => {
    isSSEStreamingRef.current = isSSEStreaming;
  }, [isSSEStreaming]);

  // Single source of truth for "is the agent actively responding?"
  // Must match exactly what the stop icon in ChatWebInput uses
  const isAgentResponding =
    primaryAgent?.mode === "planner"
      ? isSSEStreaming && chatObject?.forcePlannerStop !== true
      : isAIStreaming && chatObject?.forcePlannerStop !== true;

  // Ref for debouncing parallel step message fetches
  const pendingMessageFetchRef = useRef(null);
  const lastMessageFetchTimeRef = useRef(0);

  const {
    loading: audioProcessingInProgress,
    apiRequest: audioProcessingRequest,
    wasInvoked: audioProcessingInvoked,
    data: audioProcessingResult,
  } = useAPI(Media.processAudio, null, () => {});
  const {
    loading: videoProcessingInProgress,
    apiRequest: videoProcessingRequest,
    wasInvoked: videoProcessingInvoked,
    data: videoProcessingResult,
  } = useAPI(Media.processVideo, null, () => {});

  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);

  // EditorWrapper functionality for canvas collaboration
  const {
    value,
    editor,
    isInactive,
    updateAPIError,
    updateAPIInProgress,
    createAPIInProgress,
    saveDocument,
    handleSocketConnectionChange,
    setValue,
    setEditor,
    isLoading: editorIsLoading,
    isReadyForRTC,
    isDocumentValid,
    wordsCount,
    charactersCount,
    isSaved,
    setIsLoading: setEditorIsLoading,
    setLastCancellation,
    setIsSaveRequested,
    handleNewMRCDocument,
  } = useAutoSave({
    documentId,
    workspaceid: getActiveWorkspace()?.id,
    user,
    updateMethod: trainingAPI.updateTrainingData,
    createMethod: trainingAPI.postTrainingData,
    route: "mrc_contents",
    documentType: "readComprehensionContent",
    backupProperty: "initialCanvasValue",
    backupCommand: "SET_INITIAL_CANVAS_VALUE",
    savesSkipsForBooting: 0,
    isAGCEnabled: false,
    initialValue: [
      {
        type: "paragraph",
        children: [{ text: "" }],
      },
    ],
  });

  // Keep editorRef in sync
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Flush queued canvas operations once editor has content
  useEffect(() => {
    if (editor && editor.children?.length > 0 && pendingCanvasOpsRef.current) {
      const { canvasOps, lastAppliedChangesetID } = pendingCanvasOpsRef.current;
      pendingCanvasOpsRef.current = null;
      envConfig?.REACT_APP_ENV == "dev" &&
        console.log(
          "[Canvas] Flushing queued canvas operations after editor ready",
        );
      const result = applyCanvasOperations(
        editor,
        canvasOps,
        lastAppliedChangesetID,
      );
      if (result.success) {
        setValue(editor?.children);
        if (
          result.lastChangesetID &&
          result.lastChangesetID !== lastAppliedChangesetID
        ) {
          chatsHelper
            .updateChat(chatId, [
              {
                property: "canvasLastChangesetID",
                value: result.lastChangesetID,
              },
            ])
            .catch((error) => {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.error("Failed to update canvasLastChangesetID:", error);
            });
        }
      }
    }
  }, [editor, value]);

  useEffect(() => {
    const _id = state?.chats?.find((u) => u?.id === chatId)?.activeMessageForAI;
    loadingMessageIdRef.current = _id;
    setStreamingMessageID(_id);
    setFixedDocumentId(state?.chats?.find((u) => u?.id === chatId)?.documentId);
  }, [JSON.stringify(state?.chats?.map((u) => u?.activeMessageForAI)), chatId]);
  const onImageUploaded = (url) => {
    setImageUrl(url);
    setImageKey(url);
  };

  const extractFilesInfoFromArtifacts = (steps_artifacts) => {
    const result = [];

    // Flatten array and filter out null/undefined values
    const flattenedSteps = steps_artifacts
      .flat()
      .filter((step) => step != null);

    for (const step of flattenedSteps) {
      const { files, images, audios, videos } = step;

      if (files && files.files_keys && Array.isArray(files.files_keys)) {
        for (const fileKey of files.files_keys.filter((key) => key != null)) {
          const actualKey = Array.isArray(fileKey) ? fileKey[0] : fileKey;
          // Skip internal step files
          if (actualKey && !actualKey.includes("_internal_step")) {
            result.push({
              file_key: actualKey,
              description_from_step_definition:
                files.description_from_step_definition,
              file_type: "file",
            });
          }
        }
      }

      if (images && images.files_keys && Array.isArray(images.files_keys)) {
        for (const fileKey of images.files_keys.filter((key) => key != null)) {
          const actualKey = Array.isArray(fileKey) ? fileKey[0] : fileKey;
          // Skip internal step files
          if (actualKey && !actualKey.includes("_internal_step")) {
            result.push({
              file_key: actualKey,
              description_from_step_definition:
                images.description_from_step_definition,
              file_type: "image",
            });
          }
        }
      }

      if (audios && audios.files_keys && Array.isArray(audios.files_keys)) {
        for (const fileKey of audios.files_keys.filter((key) => key != null)) {
          const actualKey = Array.isArray(fileKey) ? fileKey[0] : fileKey;
          // Skip internal step files
          if (actualKey && !actualKey.includes("_internal_step")) {
            result.push({
              file_key: actualKey,
              description_from_step_definition:
                audios.description_from_step_definition,
              file_type: "audio",
            });
          }
        }
      }

      if (videos && videos.files_keys && Array.isArray(videos.files_keys)) {
        for (const fileKey of videos.files_keys.filter((key) => key != null)) {
          const actualKey = Array.isArray(fileKey) ? fileKey[0] : fileKey;
          // Skip internal step files
          if (actualKey && !actualKey.includes("_internal_step")) {
            result.push({
              file_key: actualKey,
              description_from_step_definition:
                videos.description_from_step_definition,
              file_type: "video",
            });
          }
        }
      }
    }
    return result;
  };
  useEffect(() => {
    // Get artifacts from chat metadata (backend source)
    let metadataArtifacts = [];
    let stepsStatus = [];
    if (chatObject?.metadata) {
      let _metadata = {};
      if (typeof chatObject?.metadata === "string")
        _metadata = JSON.parse(chatObject?.metadata);
      else {
        _metadata = chatObject?.metadata;
      }
      let _steps_artifacts = [];
      // check if string and if so convert to JSON
      if (typeof _metadata?.steps_artifacts === "string") {
        _steps_artifacts = JSON.parse(_metadata?.steps_artifacts);
      } else {
        _steps_artifacts = _metadata?.steps_artifacts;
      }
      metadataArtifacts = extractFilesInfoFromArtifacts(_steps_artifacts || []);

      // Extract step status from steps_artifacts (authoritative step completion status)
      const flattenedSteps = (_steps_artifacts || [])
        .flat()
        .filter((step) => step != null);
      stepsStatus = flattenedSteps
        .filter((step) => step?.step_id != null && step?.status)
        .map((step) => ({
          step_id: step.step_id,
          status: step.status?.toLowerCase(),
          agent_id: step.agent_id,
        }));
    }

    // Get artifacts from messages (source of truth - never lost)
    const messageArtifacts = extractAllArtifactsFromMessages(stateMessages);

    // Merge and deduplicate by file_key
    const allArtifactsMap = new Map();
    [...metadataArtifacts, ...messageArtifacts].forEach((artifact) => {
      if (artifact?.file_key && !allArtifactsMap.has(artifact.file_key)) {
        allArtifactsMap.set(artifact.file_key, artifact);
      }
    });

    setPlanArtifacts(Array.from(allArtifactsMap.values()));
    setStepsStatusFromArtifacts(stepsStatus);
  }, [JSON.stringify(chatObject), JSON.stringify(stateMessages)]);
  useEffect(() => {
    isAIStreamingRef.current = isAIStreaming;
  }, [isAIStreaming]);
  // Browser beforeunload warning when streaming
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isAgentResponding) {
        e.preventDefault();
        e.returnValue =
          "An agent is currently responding. If you leave, the response may be lost.";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isAgentResponding, t]);
  const handleCanvasCreation = async (fromAgent = false) => {
    if (canvasCreationInProgressRef.current || documentId) {
      console.log(
        "[Canvas] Skipping creation — already in progress or canvas exists",
        {
          inProgress: canvasCreationInProgressRef.current,
          documentId,
        },
      );
      return;
    }
    canvasCreationInProgressRef.current = true;
    try {
      setIsLoading(true);
      const docId = await handleNewMRCDocument(
        true,
        null,
        "New Canvas",
        null,
        "draft",
        false,
        "canvasRoot",
      );
      const _payload = [
        {
          property: "canvasID",
          value: docId,
        },
      ];

      await chatsHelper.updateChat(chatId, _payload);
      setDocumentId(docId);
      if (!fromAgent) {
        AppToasty.show(t("toasts.canvas.linked"), {
          placement: "bottom",
          type: "success",
        });
      }
    } catch (error) {
      if (!fromAgent) {
        AppToasty.show(t("toasts.canvasLinkFailed"), {
          placement: "bottom",
          type: "danger",
        });
      } else {
        console.error("[Canvas] Agent-triggered creation failed:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const _handlePullFileRequest = async (metadata) => {
    const request_id = metadata.pull_file_request_id;
    const filePath = metadata.pull_file_path;

    if (_pullPushInProgressRef.current.has(request_id)) return;
    _pullPushInProgressRef.current.add(request_id);

    try {
      const result = await handlePullFile({
        filePath,
        boundFolderPath: boundFolderPathRef.current,
        workspaceid: getActiveWorkspace()?.id,
      });

      const currentMetadata = { ...metadata };
      delete currentMetadata.pull_file_requested;
      currentMetadata.pull_file_result = {
        request_id,
        success: result.success,
        ...(result.success
          ? {
              s3_key: result.s3_key,
              filename: result.filename,
              content_type: result.content_type,
            }
          : { error: result.error }),
      };

      await chatsHelper.updateChat(chatId, [
        { property: "metadata", value: JSON.stringify(currentMetadata) },
      ]);
    } catch (error) {
      console.error("[PULL_FILE] Handler error:", error);
    } finally {
      _pullPushInProgressRef.current.delete(request_id);
    }
  };

  const _handlePushFileRequest = async (metadata) => {
    const request_id = metadata.push_file_request_id;
    const s3Key = metadata.push_file_s3_key;
    const localPath = metadata.push_file_local_path;
    const filename = metadata.push_file_filename;

    if (_pullPushInProgressRef.current.has(request_id)) return;
    _pullPushInProgressRef.current.add(request_id);

    try {
      const result = await handlePushFile({
        s3Key,
        localPath,
        filename,
        boundFolderPath: boundFolderPathRef.current,
        workspaceid: getActiveWorkspace()?.id,
      });

      const currentMetadata = { ...metadata };
      delete currentMetadata.push_file_requested;
      currentMetadata.push_file_result = {
        request_id,
        success: result.success,
        ...(result.success
          ? { local_path: result.local_path }
          : { error: result.error }),
      };

      await chatsHelper.updateChat(chatId, [
        { property: "metadata", value: JSON.stringify(currentMetadata) },
      ]);
    } catch (error) {
      console.error("[PUSH_FILE] Handler error:", error);
    } finally {
      _pullPushInProgressRef.current.delete(request_id);
    }
  };

  const handlePlanModeToggle = async (enabled) => {
    try {
      const payload = [{ property: "planMode", value: enabled }];
      await chatsHelper.updateChat(chatId, payload);
      setPlanMode(enabled);
    } catch (error) {
        AppToasty.show(t("chats.failedToUpdatePlanMode"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  const handleShowPlan = async () => {
    if (!planFile) return;
    setShowPlanModal(true);
    setPlanContentLoading(true);
    try {
      const result = await S3downloadUrlRequest({
        filename: planFile,
        context: "pdf",
        workspaceid: getActiveWorkspace()?.id,
      });
      if (result?.url) {
        const response = await fetch(result.url);
        const content = await response.text();
        setPlanContent(content);
      }
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.error("Failed to fetch plan:", error);
        AppToasty.show(t("chats.failedToLoadPlan"), {
        placement: "bottom",
        type: "danger",
      });
    } finally {
      setPlanContentLoading(false);
    }
  };
  const checkIfOllamaIsRunning = async () => {
    try {
      const response = await axios.get(`${agentHosting?.endpoint}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response?.status == 200) {
        isOllamaRunning(true);
        oLlamaModelLoaded(agentHosting?.modelName);
      } else {
        throw new Error("Ollama could not be loaded");
      }
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" && console.log("Error", error);
      AppToasty.show(
        isElectronRunning()
          ? t("chats.ollamaNotAvailableOnWeb")
          : t("chats.ollamaLoadError", {
              modelName: agentHosting?.modelName,
              endpoint: agentHosting?.endpoint,
            }),
        {
          placement: "bottom",
          type: "danger",
        },
      );
    }
  };
  useEffect(() => {
    if (agentHosting?.type == "ollama") {
      checkIfOllamaIsRunning();
    }
  }, [agentHosting?.type]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [JSON.stringify(messages)]);
  const onFileUploaded = (url) => {
    setFileUrl(url);
    setFileKey(url);
  };
  const onFilesUploaded = (
    urls,
    presignedUrls,
    indexForRemoval = null,
    removalType = "file",
  ) => {
    if (indexForRemoval !== null) {
      // Handle removal based on type
      if (removalType === "file" && fileUrls?.length > 0) {
        setFileUrls((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
        setFileKeys((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
      } else if (removalType === "audio" && audioUrls?.length > 0) {
        setAudioUrls((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
        setAudioKeys((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
      } else if (removalType === "image" && imageUrls?.length > 0) {
        setImageUrls((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
        setImageKeys((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
      }
      return;
    }

    if (!urls || !presignedUrls) return;

    // Filter files by extension
    const audioExtensions = [".wav", ".mp3", ".mp4", ".m4a", ".aac", ".ogg"];
    const imageExtensions = [
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".bmp",
      ".webp",
      ".tiff",
      ".tif",
      ".heic",
      ".heif",
      ".svg",
      ".ico",
    ];
    const documentExtensions = [
      ".pptx",
      ".ppt",
      ".json",
      ".pdf",
      ".csv",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".html",
      ".txt",
      ".md",
      ".py",
      ".js",
      ".java",
      ".cpp",
      ".c",
      ".cs",
      ".php",
      ".rb",
      ".swift",
      ".go",
      ".ts",
      ".rs",
      ".kt",
      ".scala",
      ".m",
      ".r",
      ".pl",
      ".sh",
      ".sql",
      ".vb",
      ".lua",
      ".dart",
      ".groovy",
      ".jl",
      ".hs",
      ".f90",
      ".pas",
      ".elm",
      ".clj",
    ];

    const audioFiles = { urls: [], presignedUrls: [] };
    const imageFiles = { urls: [], presignedUrls: [] };
    const documentFiles = { urls: [], presignedUrls: [] };

    urls.forEach((url, index) => {
      const extension = url.toLowerCase().substring(url.lastIndexOf("."));
      if (audioExtensions.includes(extension)) {
        audioFiles.urls.push(url);
        audioFiles.presignedUrls.push(presignedUrls[index]);
      } else if (imageExtensions.includes(extension)) {
        imageFiles.urls.push(url);
        imageFiles.presignedUrls.push(presignedUrls[index]);
      } else if (documentExtensions.includes(extension)) {
        documentFiles.urls.push(url);
        documentFiles.presignedUrls.push(presignedUrls[index]);
      }
    });

    // Set audio URLs and keys
    // Always functional-append: consecutive async uploads (e.g. rapid clipboard
    // pastes) must not read stale state and replace the previously added files.
    if (audioFiles.urls.length > 0) {
      setAudioUrls((prev) => [...(prev || []), ...audioFiles.presignedUrls]);
      setAudioKeys((prev) => [...(prev || []), ...audioFiles.urls]);
    }

    // Set image URLs and keys
    if (imageFiles.urls.length > 0) {
      setImageUrls((prev) => [...(prev || []), ...imageFiles.presignedUrls]);
      setImageKeys((prev) => [...(prev || []), ...imageFiles.urls]);
    }

    // Set document URLs and keys
    if (documentFiles.urls.length > 0) {
      setFileUrls((prev) => [...(prev || []), ...documentFiles.presignedUrls]);
      setFileKeys((prev) => [...(prev || []), ...documentFiles.urls]);
    }
  };

  const getHostingsDetails = async (primaryAgent) => {
    const hostingsData = await hostingsHelper.getHostings(
      primaryAgent?.workspaceID,
      null,
    );
    let hostings = hostingsData?.data?.HostingsByWorkspace?.items;
    hostings =
      hostings
        ?.map((u) => {
          if (u?._deleted !== true)
            return {
              ...u,
            };
        })
        ?.filter((u) => u != null) || [];
    const _localHosting = hostings?.find(
      (u) => u?.name == primaryAgent?.llmProvider,
    );
    setAgentHosting(_localHosting);
  };
  useEffect(() => {
    if (
      primaryAgent?.llmProvider != null &&
      primaryAgent?.llmProvider != "tf"
    ) {
      getHostingsDetails(primaryAgent);
    } else {
      setAgentHosting(null);
    }
  }, [primaryAgent?.id, primaryAgent?.llmProvider]);
  const onAudioUploaded = async (url) => {
    try {
      setAudioKey(url);
      setAudioUrl(url);
      const result = await audioProcessingRequest({
        token: getActiveWorkspace()?.workspaceToken,
        data: {
          audio_file: url,
          workspaceid: getActiveWorkspace()?.id,
        },
      });

      setAudiosText(result?.contents?.text);
      // AppToasty.show("Audio processed", {
      //   placement: "bottom",
      //   type: "success",
      // });
    } catch (error) {
      setAudiosText("");
      // AppToasty.show("Error processing audio", {
      //   placement: "bottom",
      //   type: "danger",
      // });
    }
  };
  const onImageSelected = (url, key) => {
    setSelectedImageKey(url);
    setSelectedImageRawKey(key);
  };
  const onVideoUploaded = async (url) => {
    try {
      setVideoKey(url);
      setVideoUrl(url);
      const result = await videoProcessingRequest({
        token: getActiveWorkspace()?.workspaceToken,
        data: {
          text_prompt: "Summarize the video in no more than 5 sentences",
          video_file: url,
          workspaceid: getActiveWorkspace()?.id,
        },
      });
      setVideosText(result?.contents);
      // AppToasty.show("Video processed", {
      //   placement: "bottom",
      //   type: "success",
      // });
    } catch (error) {
      setVideosText("");
      // AppToasty.show("Error processing video", {
      //   placement: "bottom",
      //   type: "danger",
      // });
    }
  };
  const generateUniqueMessages = (updatedMessages) => {
    const uniqueMessages = new Map();
    updatedMessages.forEach((message) => {
      const existingMessage = uniqueMessages.get(message._id);
      if (!existingMessage) {
        uniqueMessages.set(message._id, message);
      } else {
        // If an existing message with the same ID is found
        // Keep the message with the greater _lastChangedAt value
        if (message._lastChangedAt > existingMessage._lastChangedAt) {
          uniqueMessages.set(message._id, message);
        }
      }
    });
    updatedMessages = Array.from(uniqueMessages.values());
    return updatedMessages;
  };
  const handleCopy = () => {
    AppToasty.show(t("toasts.copied"), {
      placement: "bottom",
      type: "success",
    });
  };
  const handleFeedback = async (
    id,
    humanFeedback,
    humanFeedbackType,
    humanFeedbackComment,
    humanFeedbackSubType,
    humanFeedbackForInstruction = false,
  ) => {
    const payload = [
      {
        property: "humanFeedback",
        value: humanFeedback,
      },
      {
        property: "humanFeedbackProvidedBy",
        value: user?.id,
      },
      {
        property: "adminChecked",
        value: false,
      },
    ];
    if (humanFeedbackType) {
      payload.push({
        property: "humanFeedbackType",
        value: humanFeedbackType,
      });
    }
    if (humanFeedbackSubType) {
      payload.push({
        property: "humanFeedbackSubType",
        value: humanFeedbackSubType,
      });
    }
    if (humanFeedbackComment) {
      payload.push({
        property: "humanFeedbackComment",
        value: humanFeedbackComment,
      });
    }

    payload.push({
      property: "humanFeedbackForInstruction",
      value: humanFeedbackForInstruction,
    });

    try {
      const result = await messagesHelper.updateChatMessage(id, payload);
      setMessages((prevMessages) => {
        let updatedMessages = [...prevMessages];
        const newMessageIndex = updatedMessages.map((u) => u._id).indexOf(id);
        if (newMessageIndex !== -1) {
          updatedMessages[newMessageIndex] = {
            ...updatedMessages[newMessageIndex],
            humanFeedback: humanFeedback,
            humanFeedbackType: humanFeedbackType,
            humanFeedbackComment: humanFeedbackComment,
            humanFeedbackSubType: humanFeedbackSubType,
            humanFeedbackForInstruction: humanFeedbackForInstruction,
          };
        }
        return [...updatedMessages];
      });
      AppToasty.show(t("toasts.feedbackSaved"), {
        placement: "bottom",
        type: "success",
      });
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" && console.log(error);
      AppToasty.show(t("toasts.feedbackSaveFailed"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };

  // Handle base model change for the agent
  const handleBaseModelChange = async (provider, model, reasoningEffort, regionalEndpoint) => {
    if (!primaryAgent?.id) return;

    setIsModelChangeInProgress(true);
    try {
      const payload = [
        {
          property: "llmProvider",
          value: provider,
        },
        {
          property: "llmBaseModel",
          value: model,
        },
      ];
      if (reasoningEffort !== undefined) {
        payload.push({
          property: "reasoningEffort",
          value: reasoningEffort,
        });
      }
      if (regionalEndpoint !== undefined) {
        payload.push({
          property: "regionalEndpoint",
          value: regionalEndpoint,
        });
      }
      await agentsHelper.updateAgent(primaryAgent.id, payload);

      // Update local state
      const updates = {
        llmProvider: provider,
        llmBaseModel: model,
      };
      if (reasoningEffort !== undefined) updates.reasoningEffort = reasoningEffort;
      if (regionalEndpoint !== undefined) updates.regionalEndpoint = regionalEndpoint;

      setPrimaryAgent((prev) => ({
        ...prev,
        ...updates,
      }));

      // Update agents in global state
      dispatch(
        "SET_AGENTS",
        state?.agents?.map((agent) =>
          agent.id === primaryAgent.id
            ? { ...agent, ...updates }
            : agent,
        ),
      );

      AppToasty.show(t("toasts.baseModelUpdated"), {
        placement: "bottom",
        type: "success",
      });
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.log("Error updating base model:", error);
      AppToasty.show(t("toasts.baseModelUpdateFailed"), {
        placement: "bottom",
        type: "danger",
      });
    } finally {
      setIsModelChangeInProgress(false);
    }
  };

  // Handle toggling agent tool capabilities
  const handleToolToggle = async (property, currentValue) => {
    if (!primaryAgent?.id) return;

    // If a toggle is already in progress, queue this one
    if (toolToggleInProgress) {
      pendingToolTogglesRef.current.push({ property, currentValue });
      return;
    }

    setToolToggleInProgress(property);

    try {
      const payload = [];
      const stateUpdates = {};

      // Special case: Agentic cycle toggle - cycles through 3 states
      // This is a virtual property, not saved to backend
      if (property === "agenticCycle") {
        const currentState = getAgenticCycleState(primaryAgent);
        const nextState = getNextAgenticCycleState(currentState);

        // Always clear both properties first
        payload.push({ property: "agenticRAG", value: false });
        payload.push({ property: "useInterleavedReasoning", value: false });
        stateUpdates.agenticRAG = false;
        stateUpdates.useInterleavedReasoning = false;

        // Set new state based on cycle position
        if (nextState === AGENTIC_CYCLE_STATES.AGENTIC_INTERLEAVED) {
          payload.push({ property: "useInterleavedReasoning", value: true });
          stateUpdates.useInterleavedReasoning = true;
        }
        // If NONE, both remain false (already set above)
      } else {
        // Regular property toggle
        const latestValue = primaryAgent?.[property];
        const newValue = !latestValue;

        envConfig?.REACT_APP_ENV === "dev" &&
          console.log(
            `[ToolToggle] ${property}: ${latestValue} -> ${newValue}`,
          );

        payload.push({
          property: property,
          value: newValue,
        });
        stateUpdates[property] = newValue;

        // If disabling, check for cascading disables
        if (!newValue) {
          const cascadingDisables = getCascadingDisables(property);
          cascadingDisables.forEach((cascadeProperty) => {
            if (primaryAgent?.[cascadeProperty]) {
              payload.push({
                property: cascadeProperty,
                value: false,
              });
              stateUpdates[cascadeProperty] = false;
            }
          });
        }

        // Special case: When enabling code execution (hasCode) in retriever mode, also enable agenticRAG
        if (
          property === "hasCode" &&
          newValue &&
          primaryAgent?.mode === "retriever"
        ) {
          if (!primaryAgent?.agenticRAG) {
            payload.push({
              property: "agenticRAG",
              value: true,
            });
            stateUpdates["agenticRAG"] = true;
          }
        }
      }

      await agentsHelper.updateAgent(primaryAgent.id, payload);

      // Update local state with all changes
      setPrimaryAgent((prev) => ({
        ...prev,
        ...stateUpdates,
      }));

      // Update agents in global state
      dispatch(
        "SET_AGENTS",
        state?.agents?.map((agent) =>
          agent.id === primaryAgent.id ? { ...agent, ...stateUpdates } : agent,
        ),
      );
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.log("Error toggling tool:", error);
      AppToasty.show(t("toasts.toolSettingUpdateFailed"), {
        placement: "bottom",
        type: "danger",
      });
    } finally {
      setToolToggleInProgress(null);

      // Process next queued toggle if any
      if (pendingToolTogglesRef.current.length > 0) {
        const nextToggle = pendingToolTogglesRef.current.shift();
        // Use setTimeout to ensure state is updated before processing next
        // Pass null for currentValue since handleToolToggle now reads directly from primaryAgent
        setTimeout(() => {
          handleToolToggle(nextToggle.property, null);
        }, 0);
      }
    }
  };

  const handleHandOffToggle = async (newValue) => {
    if (!chatId) return;

    try {
      await chatsHelper.updateChat(chatId, [
        { property: "handedOffToHuman", value: newValue },
      ]);

      // Update local state
      dispatch(
        "SET_CHATS",
        state?.chats?.map((chat) =>
          chat.id === chatId ? { ...chat, handedOffToHuman: newValue } : chat,
        ),
      );
      setChatObject((prev) => ({ ...prev, handedOffToHuman: newValue }));
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.log("Error toggling hand off:", error);
      AppToasty.show(t("toasts.handOffUpdateFailed"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };

  const handleOnRecorderClick = (v) => {
    try {
      if (v) {
        startRecording();
        // show the toast message
        AppToasty.show(t("chats.recordingStarted"), {
          placement: "bottom",
          type: "success",
        });
      } else {
        stopRecording();
        AppToasty.show(t("chats.recordingStopped"), {
          placement: "bottom",
          type: "success",
        });
      }
    } catch (error) {
        AppToasty.show(t("chats.recordingError"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };

  // Editor error handler for real-time collaboration
  const handleEditorError = (event) => {
    envConfig?.REACT_APP_ENV == "dev" && console.error("Editor error", event);
    if (event.message) {
      AppToasty.show(t(event.message), {
        type: "danger",
        placement: "bottom",
      });
    }
  };

  // Canvas selection handler
  const onSelectionQuantumEditor = (selectionData) => {
    if (selectionData) {
      // Store the entire selection object with anchor/focus positions
      setCanvasSelection(selectionData);
    }
  };

  function safeJsonParse(str, fallback = null) {
    if (!str || typeof str !== "string") return str || fallback;
    try {
      return JSON.parse(str);
    } catch (e) {
      console.warn("[SSE] Failed to parse JSON string:", e.message);
      return fallback;
    }
  }

  // Merge an incoming SSE metadata object into the current message metadata,
  // PRESERVING backend-authoritative fields (step_id, from_planner,
  // planner_execution_plan) when the incoming update omits them. Without this,
  // a partial SSE update (e.g. an agent_processing_status event that carries
  // metadata WITHOUT step_id) replaces the whole metadata object and wipes the
  // previously-stamped step_id — which flipped shouldShowInChat between the
  // main chat and the steps panel for the SAME message id while a sub-agent ran.
  const PLANNER_AUTHORITATIVE_META_KEYS = [
    "step_id",
    "from_planner",
    "planner_execution_plan",
  ];
  const normalizeMeta = (m) =>
    m && typeof m === "object" && !Array.isArray(m) ? m : {};
  function mergePlannerMetadata(currentMeta, incomingMeta) {
    const current = normalizeMeta(currentMeta);
    const incoming = normalizeMeta(incomingMeta);
    const merged = { ...current, ...incoming };
    for (const key of PLANNER_AUTHORITATIVE_META_KEYS) {
      if (current[key] !== undefined && incoming[key] === undefined) {
        merged[key] = current[key];
      }
    }
    return merged;
  }

  function removeSpecificUnicodeAtEnd(text) {
    if (text) {
      const unicodeRegex = /(\u26AB|\u26AA)$/g;
      return text.replace(unicodeRegex, "");
    } else {
      return "";
    }
  }
  const onCancelGeneration = async () => {
    // Handle SSE streaming cancellation (used by both chatter and planner modes)
    if (isSSEStreaming) {
      cancelSSEStream();
      // Note: isSSEStreaming will automatically become false after cancelSSEStream()
      // For non-planner modes, also reset legacy state (can be removed once all modes use SSE)
      if (primaryAgent?.mode !== "planner") {
        setIsAIStreaming(false);
        setAllowStop(false);
      }
      setWaitingForFirstResponse(false);
      setWaitingForFirstChunk(false);
      // Update message status to cancelled
      if (streamingMessageID) {
        await messagesHelper.updateChatMessage(streamingMessageID, [
          { property: "status", value: "cancelled" },
        ]);
        // Update local message state with accumulated text (always update loading state)
        const accumulatedText = getAccumulatedText();
        setMessages((prevMessages) => {
          const messageIndex = prevMessages.findIndex(
            (m) => m._id === streamingMessageID,
          );
          if (messageIndex === -1) return prevMessages;
          const updatedMessages = [...prevMessages];
          updatedMessages[messageIndex] = {
            ...updatedMessages[messageIndex],
            text: accumulatedText
              ? removeSpecificUnicodeAtEnd(accumulatedText)
              : updatedMessages[messageIndex].text,
            loading: false,
          };
          return updatedMessages;
        });
      }
      const cancelPayload = [
        { property: "isAIReplying", value: false },
        { property: "activeMessageForAI", value: null },
      ];
      // Add forcePlannerStop for planner mode
      if (
        primaryAgent?.mode === "planner" ||
        primaryAgent?.mode === "computer"
      ) {
        cancelPayload.push({ property: "forcePlannerStop", value: true });
        AppToasty.show(t("chats.planExecutionStopRequested"), {
          placement: "bottom",
          type: "success",
        });
      }
      await chatsHelper.updateChat(chatId, cancelPayload);
      return;
    }
    // Handle Ollama local streaming cancellation
    if (localStreamingController?.current) {
      localStreamingController.current.abort();
      localStreamingController.current = null;
      setIsAIStreaming(false);
      setAllowStop(false);
    } else {
      subscriptionObj?.unsubscribe();
      setIsSubscriptionRequired(true);
      setIsAIStreaming(false);
      setLastUpdateMessageTime(null);
      const _payload = [
        {
          property: "isAIReplying",
          value: false,
        },
        {
          property: "activeMessageForAI",
          value: null,
        },
      ];
      if (primaryAgent?.mode == "planner" || primaryAgent?.mode == "computer")
        _payload.push({
          property: "forcePlannerStop",
          value: true,
        });

      await chatsHelper.updateChat(chatId, _payload);
      const result = await messagesHelper.updateChatMessage(
        streamingMessageID,
        [
          {
            property: "status",
            value: "cancelled",
          },
        ],
      );
      setChatObject({
        ...chatObject,
        isAIReplying: false,
        activeMessageForAI: null,
        forcePlannerStop:
          primaryAgent?.mode == "planner" || primaryAgent?.mode == "computer"
            ? true
            : false,
      });
      if (primaryAgent?.mode == "planner" || primaryAgent?.mode == "computer") {
        AppToasty.show(t("chats.planExecutionStopRequested"), {
          placement: "bottom",
          type: "success",
        });
      }
    }
  };
  const handleClickOnWidgetChatsHistory = () => {
    if (getPlanConfig()?.isWidgetChatsHistoryEnabled) {
      // Check if AI is streaming and warn user before navigating
      if (isAgentResponding) {
        const warningMessage =
          "An agent is currently responding. If you leave now, the response may be lost. Do you want to continue?";
        const confirmed = window.confirm(warningMessage);
        if (!confirmed) {
          return;
        }
        // User confirmed, cancel the stream before navigating
        if (isSSEStreaming) {
          cancelSSEStream();
        }
        setIsAIStreaming(false);
      }
      setSelectedNode(null);
      history.push(`/workspaces/${getActiveWorkspace()?.id}/${_routeBase}`);
      loadChats(!showWidgetChatsHistory, true);
      setShowWidgetChatsHistory(!showWidgetChatsHistory);
    } else {
      AppToasty.show(t("chats.widgetChatsHistoryNotEnabled"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  useEffect(() => {
    let subscriptionObjCreate;
    const setupSubscription = async () => {
      if (chatId) {
        if (isConnectedToCloud()) setErrorWithSubscription(false);
        setAllowStop(false);
        subscriptionObjCreate = await subscribeGraphQL(
          graphqlOperation(onCreateChatMessageByChatIDEvent, {
            chatID: chatId,
          }),
          {
            next: async (data) => {
              setIsCreateSubscriptionHealthy(true);
              const mainMessageObj =
                data?.value?.data?.onCreateChatMessageByChatIDEvent;

              const isPlannerModeLocal =
                primaryAgentModeRef.current === "planner" ||
                primaryAgentModeRef.current === "computer";

              envConfig?.REACT_APP_ENV == "dev" &&
                console.log("[Subscription] onCreate received:", {
                  messageId: mainMessageObj?.id,
                  role: mainMessageObj?.role,
                  isPlannerMode: isPlannerModeLocal,
                  isSSEStreaming: isSSEStreamingRef.current,
                  chatID: mainMessageObj?.chatID,
                  currentChatId: chatId,
                  status: mainMessageObj?.status,
                  agentID: mainMessageObj?.agentID,
                });

              if (mainMessageObj?.chatID != chatId) {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log(
                    "[Subscription] onCreate skipped - chatID mismatch",
                  );
                return;
              }

              // Check if message already exists (SSE may have created it first)
              const messageAlreadyExists = messagesRef.current?.find(
                (m) => m._id === mainMessageObj?.id,
              );
              if (messageAlreadyExists) {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log(
                    "[Subscription] onCreate skipped - message already exists (created by SSE):",
                    mainMessageObj?.id,
                    { isPlannerMode: isPlannerModeLocal },
                  );
                return;
              }

              // Skip user message creation events when SSE streaming is active in NON-PLANNER mode
              // SSE creates optimistic user message, backend also creates one - skip to avoid duplication
              // In PLANNER mode, we need subscription to add messages since sub-agents create them via Lambda
              if (
                isSSEStreamingRef.current &&
                mainMessageObj?.role === "user" &&
                !isPlannerModeLocal
              ) {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log(
                    "[Subscription] onCreate skipped - SSE user message (non-planner):",
                    mainMessageObj?.id,
                  );
                return;
              }

              envConfig?.REACT_APP_ENV == "dev" &&
                console.log(
                  "[Subscription] onCreate PROCEEDING to add message:",
                  mainMessageObj?.id,
                  {
                    isPlannerMode: isPlannerModeLocal,
                    role: mainMessageObj?.role,
                  },
                );

              lastMessageCreatedRef.current = mainMessageObj?.id;
              const message = mainMessageObj?.text;
              // Determine display name based on role (same logic as adaptMessages)
              const displayName =
                mainMessageObj?.role === "assistant"
                  ? "assistant"
                  : getUserName(mainMessageObj?.userID) || mainMessageObj?.role;
              // While SSE is streaming in non-planner mode, an AI placeholder
              // message arriving via this subscription must be stamped as
              // loading, otherwise the TypingText indicator is skipped and the
              // placeholder text renders as a static bubble for the whole
              // pre-token window (race with the SSE message_id event).
              const isPlaceholderMessage = [null, "", " ", "...", "**...**", "~"].includes(message);
              const newMessage = {
                ...mainMessageObj,
                _id: mainMessageObj.id,
                createdAt: mainMessageObj.creationTime,
                _lastChangedAt: mainMessageObj?._lastChangedAt,
                status: mainMessageObj?.status,
                user: {
                  id: mainMessageObj?.userID,
                  name: displayName,
                  agentName:
                    primaryAgent?.id != mainMessageObj?.agentID
                      ? getAgent(mainMessageObj?.agentID)?.label
                      : primaryAgent?.label,
                },
                text: `${message}`,
                loading:
                  isSSEStreamingRef.current &&
                  !isPlannerModeLocal &&
                  mainMessageObj?.isAI === true &&
                  isPlaceholderMessage,
                humanFeedback: mainMessageObj?.humanFeedback,
                humanFeedbackType: mainMessageObj?.humanFeedbackType,
                humanFeedbackSubType: mainMessageObj?.humanFeedbackSubType,
                humanFeedbackComment: mainMessageObj?.humanFeedbackComment,
                metadata:
                  typeof mainMessageObj.metadata === "string"
                    ? JSON.parse(mainMessageObj.metadata)
                    : mainMessageObj.metadata,
                callbackMetadata:
                  typeof mainMessageObj.callbackMetadata === "string"
                    ? JSON.parse(mainMessageObj.callbackMetadata)
                    : mainMessageObj.callbackMetadata,
                agentName: getAgent(mainMessageObj?.agentID)?.label,
              };
              setMessages((prevMessages) => {
                let updatedMessages = [...prevMessages];
                if (
                  prevMessages?.find((u) => u?._id == newMessage?._id) == null
                )
                  updatedMessages.push(newMessage);
                updatedMessages = updatedMessages.sort((a, b) => {
                  return new Date(a.createdAt) - new Date(b.createdAt);
                });
                return [...updatedMessages];
              });
            },
            error: (error) => {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log(`Error with subscription: ${chatId}`, error);
              setIsCreateSubscriptionHealthy(false);
            },
            close: () => {
              setIsSocketConnected(false);
              setIsCreateSubscriptionHealthy(false);
            },
            connect: () => {
              setIsSocketConnected(true);
              setIsCreateSubscriptionHealthy(true);
            },
          },
        );
        createSubscriptionRef.current = subscriptionObjCreate;
        subscribeToChatMessages(chatId);
      }
    };
    setupSubscription();
    return () => {
      setIsSocketConnected(false);
      setIsCreateSubscriptionHealthy(false);
      if (subscriptionObjCreate && subscriptionObjCreate?.unsubscribe)
        subscriptionObjCreate?.unsubscribe();
      createSubscriptionRef.current = null;
    };
  }, [chatId, isSendingEventRef?.current]);
  const subscribeToPlanner = (chatId, streamingMessageID) => {
    const setupSubscription = async () => {
      const sub = await subscribeGraphQL(
        graphqlOperation(onUpdateChatPlanner, {
          id: chatId,
        }),
        {
          next: async (data) => {
            setIsChatSubscriptionHealthy(true);
            let chatObj = data?.value?.data?.onUpdateChat;
            if (chatObj?.id != chatId) return;
            if (
              !lastChangedChatRef.current ||
              chatObj?._lastChangedAt > lastChangedChatRef.current
            )
              lastChangedChatRef.current = chatObj?._lastChangedAt;
            else if (chatObj?.id === chatId) {
              if (primaryAgent?.mode == "planner")
                setIsAIStreaming(
                  chatObj?.isAIReplying === true &&
                    chatObj?.metadata?.planning_status !==
                      "plan_execution_completed" &&
                    chatObj?.metadata?.planning_status !==
                      "plan_execution_failed",
                );
              else {
                setIsAIStreaming(chatObj?.isAIReplying === true);
              }
              return;
            } else return;
            if (chatObj?.metadata && typeof chatObj?.metadata === "string")
              chatObj.metadata = JSON.parse(chatObj.metadata);
            try {
              const objectIndex = state?.chats?.findIndex(
                (log) => log.id === chatObj?.id,
              );
              if (objectIndex >= 0 && state?.chats?.length > 1) {
                const updatedProperty = [...state["chats"]];
                updatedProperty[objectIndex] = chatObj;
                dispatch("SET_CHATS", updatedProperty);
              }
            } catch (error) {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log(
                  "Failed to update the chats using subscriptions",
                  error,
                );
            }

            // update chat title if it has changed
            if (chatObj?.id === chatId) {
              if (
                chatObj?.name !== chatObject?.name ||
                JSON.stringify(chatObj?.metadata) !==
                  JSON.stringify(chatObject?.metadata)
              ) {
                setChatObject(chatObj);
              }
              if (
                chatObj?.primaryRole &&
                chatObj?.primaryRole !== chatObject?.primaryRole
              ) {
                if (getAgent(chatObj?.primaryRole))
                  setPrimaryAgent(getAgent(chatObj?.primaryRole));
              }
              if (chatObj?.metadata) {
                // if metadata is a sting, parse it
                if (typeof chatObj?.metadata === "string") {
                  chatObj.metadata = JSON.parse(chatObj?.metadata);
                }
                // setPlanningExecution(chatObj?.metadata?.planning_status);
                //  possible planning_status values: planning_not_available, planning_requires_approval, planning_approved, plan_execution_in_progress, plan_execution_completed, plan_execution_failed,
                if (chatObj?.isAIReplying === true) {
                  if (typeof chatObj?.metadata?.plan === "string")
                    chatObj.metadata.plan = JSON.parse(chatObj?.metadata?.plan);
                  setIsAIStreaming(true);
                  setActiveStep(chatObj?.metadata?.active_step);
                  setTotalSteps(chatObj?.metadata?.plan?.length || 0);
                }
              }

              // Process canvas operations if present
              if (chatObj?.canvasOperations) {
                const canvasOps =
                  typeof chatObj.canvasOperations === "string"
                    ? JSON.parse(chatObj.canvasOperations)
                    : chatObj.canvasOperations;

                const currentEditor = editorRef.current;
                const editorReady =
                  currentEditor && currentEditor.children?.length > 0;
                console.log(
                  "[Canvas] Subscription received canvasOperations:",
                  {
                    hasEditor: !!currentEditor,
                    editorReady,
                    editorChildrenCount: currentEditor?.children?.length,
                    documentId,
                  },
                );
                if (canvasOps && editorReady) {
                  // Get the last applied changeset ID from the chat object
                  const lastAppliedChangesetID =
                    chatObj?.canvasLastChangesetID ||
                    chatObject?.canvasLastChangesetID;
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log(
                      "Last applied changeset ID:",
                      lastAppliedChangesetID,
                    );

                  // Apply only new changesets
                  const result = applyCanvasOperations(
                    currentEditor,
                    canvasOps,
                    lastAppliedChangesetID,
                  );

                  if (result.success) {
                    // Update the editor value after applying operations
                    setValue(currentEditor?.children);

                    // Update the chat with the new last changeset ID if it changed
                    if (
                      result.lastChangesetID &&
                      result.lastChangesetID !== lastAppliedChangesetID
                    ) {
                      // Save the new canvasLastChangesetID to the chat
                      chatsHelper
                        .updateChat(chatId, [
                          {
                            property: "canvasLastChangesetID",
                            value: result.lastChangesetID,
                          },
                        ])
                        .catch((error) => {
                          envConfig?.REACT_APP_ENV == "dev" &&
                            console.error(
                              "Failed to update canvasLastChangesetID:",
                              error,
                            );
                        });
                    }
                  } else if (result.error) {
                    envConfig?.REACT_APP_ENV == "dev" &&
                      console.error(
                        "Failed to apply canvas operations:",
                        result.error,
                      );
                  }
                } else if (canvasOps) {
                  // Editor not ready yet — queue for when it mounts
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log(
                      "[Canvas] Editor not ready, queuing canvas operations",
                    );
                  pendingCanvasOpsRef.current = {
                    canvasOps,
                    lastAppliedChangesetID:
                      chatObj?.canvasLastChangesetID ||
                      chatObject?.canvasLastChangesetID,
                  };
                }
              }
              // Detect canvas_init_requested from agent — trigger canvas creation (once only)
              if (
                chatObj?.metadata?.canvas_init_requested &&
                !chatObj?.canvasID &&
                !canvasCreationInProgressRef.current
              ) {
                console.log(
                  "[Canvas] Agent requested canvas creation via metadata",
                );
                handleCanvasCreation(true);
              }

              // Detect pull_file_requested from agent — trigger file pull
              if (
                chatObj?.metadata?.pull_file_requested &&
                !chatObj?.metadata?.pull_file_result
              ) {
                console.log("[PULL_FILE] Agent requested file pull:", {
                  path: chatObj.metadata.pull_file_path,
                  request_id: chatObj.metadata.pull_file_request_id,
                });
                _handlePullFileRequest(chatObj.metadata);
              }

              // Detect push_file_requested from agent — trigger file push
              if (
                chatObj?.metadata?.push_file_requested &&
                !chatObj?.metadata?.push_file_result
              ) {
                console.log("[PUSH_FILE] Agent requested file push:", {
                  s3_key: chatObj.metadata.push_file_s3_key,
                  local_path: chatObj.metadata.push_file_local_path,
                  request_id: chatObj.metadata.push_file_request_id,
                });
                _handlePushFileRequest(chatObj.metadata);
              }

              setPlanFile(chatObj?.planFile || null);
              setPlanStatus(chatObj?.planStatus || null);
              if (primaryAgent?.mode == "planner")
                setIsAIStreaming(
                  chatObj?.isAIReplying === true &&
                    chatObj?.metadata?.planning_status !==
                      "plan_execution_completed" &&
                    chatObj?.metadata?.planning_status !==
                      "plan_execution_failed",
                );
              else {
                setIsAIStreaming(chatObj?.isAIReplying === true);
              }
            }
          },
          error: (error) => {
            envConfig?.REACT_APP_ENV == "dev" &&
              console.log(`Error with chat subscription: ${chatId}`, error);
            setIsChatSubscriptionHealthy(false);
          },
          close: () => {
            setIsChatSubscriptionHealthy(false);
          },
          connect: () => {
            setIsChatSubscriptionHealthy(true);
          },
        },
      );
      return sub;
    };
    return setupSubscription();
  };
  useEffect(() => {
    streamingMessageIDRef.current = streamingMessageID;
  }, [streamingMessageID]);
  const subscribeToChatMessages = (
    chatId,
    streamingMessageID,
    primaryAgent,
  ) => {
    const messageQueue = [];
    let lastProcessedTimestamp = {};
    let isProcessingQueue = false;
    let retryCount = 0;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000;

    const setupSubscription = async () => {
      try {
        const sub = await subscribeGraphQL(
          graphqlOperation(onUpdateChatMessageByChatIDEvent, {
            chatID: chatId,
          }),
          {
            next: async (data) => {
              setIsSubscriptionHealthy(true);
              try {
                const mainMessageObj =
                  data?.value?.data?.onUpdateChatMessageByChatIDEvent;
                if (!mainMessageObj || mainMessageObj?.chatID !== chatId)
                  return;

                const timestamp = mainMessageObj._lastChangedAt;
                const messageId = mainMessageObj.id;

                if (!timestamp || !messageId) return;

                // Deduplicate messages by checking both ID and timestamp
                const existingMessageIndex = messageQueue.findIndex(
                  (item) => item.messageId === messageId,
                );

                const lastTimestamp = lastProcessedTimestamp[messageId] || 0;
                if (existingMessageIndex === -1 || timestamp > lastTimestamp) {
                  // Remove any existing message with same ID
                  if (existingMessageIndex !== -1) {
                    messageQueue.splice(existingMessageIndex, 1);
                  }

                  messageQueue.push({ timestamp, mainMessageObj, messageId });
                  messageQueue.sort((a, b) => a.timestamp - b.timestamp);

                  // Reset retry count on successful message
                  retryCount = 0;
                }

                processQueue();
              } catch (error) {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.error("Error processing subscription data:", error);
              }
            },
            error: (error) => {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.error(
                  `Error with subscription for chat ${chatId}:`,
                  error,
                );
              setErrorWithSubscription(true);
              setIsSubscriptionHealthy(false);

              // Implement exponential backoff retry
              if (retryCount < MAX_RETRIES) {
                retryCount++;
                const delay = RETRY_DELAY * Math.pow(2, retryCount - 1);
                setTimeout(() => {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log(
                      `Retrying subscription (attempt ${retryCount}/${MAX_RETRIES})`,
                    );
                  setupSubscription();
                }, delay);
              } else {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.error("Max retries reached for subscription");
                if (lastSubscribedChatId < 10)
                  setLastSubscribedChatId((prev) => prev + 1);
              }
            },
            close: () => {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log("Subscription closed");
              setIsSocketConnected(false);
              setIsSubscriptionHealthy(false);
            },
            connect: () => {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log("Subscription connected");
              setIsSocketConnected(true);
              setIsSubscriptionHealthy(true);
              retryCount = 0;
            },
          },
        );
        return sub;
      } catch (error) {
        envConfig?.REACT_APP_ENV == "dev" &&
          console.error("Failed to setup subscription:", error);
        setErrorWithSubscription(true);
        throw error;
      }
    };

    const processQueue = () => {
      if (isProcessingQueue || messageQueue.length === 0) return;

      isProcessingQueue = true;

      const processNextMessage = async () => {
        if (messageQueue.length === 0) {
          isProcessingQueue = false;
          return;
        }

        const { timestamp, mainMessageObj, messageId } = messageQueue.shift();

        // Only process if this is newer than what we've already processed
        const lastTimestamp = lastProcessedTimestamp[messageId] || 0;
        if (timestamp > lastTimestamp) {
          lastProcessedTimestamp[messageId] = timestamp;

          try {
            await processMessages(mainMessageObj);
          } catch (error) {
            envConfig?.REACT_APP_ENV == "dev" &&
              console.error("Error processing message:", error);
          }
        }

        // Use requestAnimationFrame for better performance
        requestAnimationFrame(processNextMessage);
      };

      processNextMessage();
    };

    const processMessages = async (mainMessageObj) => {
      const message = mainMessageObj?.text;
      const _streamingMessageID = streamingMessageIDRef?.current;
      const messageId = mainMessageObj?.id;
      const isPlannerMode =
        primaryAgentModeRef.current === "planner" ||
        primaryAgentModeRef.current === "computer";

      envConfig?.REACT_APP_ENV == "dev" &&
        console.log("[Subscription] processMessages (onUpdate) called:", {
          messageId,
          isPlannerMode,
          isSSEStreaming: isSSEStreamingRef.current,
          status: mainMessageObj?.status,
          role: mainMessageObj?.role,
          isAI: mainMessageObj?.isAI,
          agentID: mainMessageObj?.agentID,
          textPreview: message?.substring(0, 50),
          _streamingMessageID,
        });

      if (!messageId) {
        envConfig?.REACT_APP_ENV == "dev" &&
          console.log("[Subscription] processMessages skipped - no messageId");
        return;
      }

      // Update timestamp for streaming message tracking
      if (messageId === streamingMessageID) {
        setLastUpdateMessageTime(Date.now());
      }

      // Helper to parse metadata safely
      const parseMetadata = (data) =>
        typeof data === "string" ? JSON.parse(data) : data;

      setMessages((prevMessages) => {
        const messageIndex = prevMessages.findIndex(
          (msg) => msg._id === messageId,
        );

        // Message not found - skip to prevent duplicates
        if (messageIndex === -1) {
          envConfig?.REACT_APP_ENV == "dev" &&
            console.log(
              "[Subscription] processMessages - MESSAGE NOT FOUND in stack:",
              {
                messageId,
                isPlannerMode,
                existingMessageIds: prevMessages.map((m) => m._id),
                status: mainMessageObj?.status,
              },
            );
          return prevMessages;
        }

        envConfig?.REACT_APP_ENV == "dev" &&
          console.log(
            "[Subscription] processMessages - message found at index:",
            messageIndex,
            { messageId, isPlannerMode },
          );

        const currentMessage = prevMessages[messageIndex];
        const newMessage = { ...currentMessage };
        let hasChanges = false;

        // ALWAYS process callback metadata updates (suggestions/nextQuestion)
        // These come after streaming completes and need to be shown regardless of SSE state
        if (
          mainMessageObj.status === "fulfilled" &&
          mainMessageObj?.callbackMetadata
        ) {
          const callbackMeta = parseMetadata(mainMessageObj.callbackMetadata);
          if (
            callbackMeta?._type === "suggestion" ||
            callbackMeta?._type === "nextQuestion"
          ) {
            newMessage.callbackMetadata = callbackMeta;
            hasChanges = true;
          }
        }

        // When SSE is active, only process callback metadata (above)
        // SSE handles all text streaming for chatter mode
        if (isSSEStreamingRef.current && !isPlannerMode) {
          envConfig?.REACT_APP_ENV == "dev" &&
            console.log(
              "[Subscription] processMessages - SSE active in non-planner mode, skipping:",
              { messageId, hasChanges },
            );
          if (hasChanges) {
            const updatedMessages = [...prevMessages];
            updatedMessages[messageIndex] = newMessage;
            return updatedMessages;
          }
          return prevMessages;
        }

        // PLANNER MODE: Full subscription-based streaming
        if (isPlannerMode) {
          const isStreamingMessage = messageId === _streamingMessageID;

          envConfig?.REACT_APP_ENV == "dev" &&
            console.log(
              "[Subscription] processMessages - PLANNER MODE processing:",
              {
                messageId,
                status: mainMessageObj.status,
                isAI: mainMessageObj?.isAI,
                isStreamingMessage,
                _streamingMessageID,
                textPreview: message?.substring(0, 50),
                metadata: mainMessageObj?.metadata,
              },
            );

          // Handle fulfilled messages
          if (mainMessageObj.status === "fulfilled") {
            envConfig?.REACT_APP_ENV == "dev" &&
              console.log(
                "[Subscription] processMessages - planner: handling fulfilled message:",
                messageId,
              );
            if (isStreamingMessage) {
              setStreamingMessageID(null);
              chatsHelper.updateChat(selectedNode?.id, [
                { property: "isAIReplying", value: true },
                { property: "activeMessageForAI", value: null },
              ]);
            }

            newMessage.text = removeSpecificUnicodeAtEnd(message);
            newMessage.loading = false;
            newMessage.status = mainMessageObj?.status;
            newMessage.metadata = parseMetadata(mainMessageObj.metadata);
            newMessage.callbackMetadata = parseMetadata(
              mainMessageObj.callbackMetadata,
            );
            newMessage.images = mainMessageObj?.images;
            newMessage.audios = mainMessageObj?.audios;
            newMessage.videos = mainMessageObj?.videos;
            newMessage.files = mainMessageObj?.files;
            newMessage.agentName = getAgent(mainMessageObj?.agentID)?.label;
            hasChanges = true;
          }
          // Handle in-progress streaming updates
          else if (mainMessageObj.isAI === true) {
            envConfig?.REACT_APP_ENV == "dev" &&
              console.log(
                "[Subscription] processMessages - planner: handling in-progress AI message:",
                messageId,
              );
            setAllowStop(loadingMessageIdRef?.current === messageId);

            // Metadata-only update (text is placeholder)
            if (mainMessageObj.text === "**...**" && mainMessageObj.metadata) {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log(
                  "[Subscription] processMessages - planner: metadata-only update:",
                  messageId,
                );
              const currentText = currentMessage?.text || "";
              newMessage.text = `${removeSpecificUnicodeAtEnd(currentText)}${
                isAIStreamingRef.current ? " " + cursor : ""
              }`;
              newMessage.metadata = parseMetadata(mainMessageObj.metadata);
            }
            // Text update
            else if (message !== "**...**") {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log(
                  "[Subscription] processMessages - planner: text update:",
                  messageId,
                );
              newMessage.text = `${removeSpecificUnicodeAtEnd(message)}${
                isAIStreamingRef.current ? " " + cursor : ""
              }`;
              newMessage.status = mainMessageObj?.status;
              newMessage.metadata = parseMetadata(mainMessageObj.metadata);
              newMessage.images = mainMessageObj?.images;
              newMessage.audios = mainMessageObj?.audios;
              newMessage.videos = mainMessageObj?.videos;
              newMessage.files = mainMessageObj?.files;
            }
            newMessage.loading = false;
            newMessage.status = mainMessageObj?.status;
            newMessage.agentName = getAgent(mainMessageObj?.agentID)?.label;
            hasChanges = true;
          } else {
            envConfig?.REACT_APP_ENV == "dev" &&
              console.log(
                "[Subscription] processMessages - planner: message DID NOT MATCH any condition:",
                {
                  messageId,
                  status: mainMessageObj.status,
                  isAI: mainMessageObj?.isAI,
                  role: mainMessageObj?.role,
                },
              );
          }
        }
        // NON-PLANNER MODE: Sync final state from subscription
        // SSE handles streaming, but we sync final fulfilled state for consistency
        else if (mainMessageObj.status === "fulfilled") {
          newMessage.text = removeSpecificUnicodeAtEnd(message);
          newMessage.loading = false;
          newMessage.status = mainMessageObj?.status;
          newMessage.metadata = parseMetadata(mainMessageObj.metadata);
          newMessage.callbackMetadata = parseMetadata(
            mainMessageObj.callbackMetadata,
          );
          newMessage.images = mainMessageObj?.images;
          newMessage.audios = mainMessageObj?.audios;
          newMessage.videos = mainMessageObj?.videos;
          newMessage.files = mainMessageObj?.files;
          newMessage.humanFeedback = mainMessageObj?.humanFeedback;
          newMessage.humanFeedbackType = mainMessageObj?.humanFeedbackType;
          newMessage.humanFeedbackSubType =
            mainMessageObj?.humanFeedbackSubType;
          newMessage.humanFeedbackComment =
            mainMessageObj?.humanFeedbackComment;
          newMessage.agentName = getAgent(mainMessageObj?.agentID)?.label;
          hasChanges = true;
        }

        if (hasChanges) {
          envConfig?.REACT_APP_ENV == "dev" &&
            console.log(
              "[Subscription] processMessages - UPDATING message in stack:",
              { messageId, isPlannerMode },
            );
          const updatedMessages = [...prevMessages];
          updatedMessages[messageIndex] = newMessage;
          return updatedMessages;
        }

        envConfig?.REACT_APP_ENV == "dev" &&
          console.log(
            "[Subscription] processMessages - NO CHANGES, returning prevMessages:",
            { messageId, isPlannerMode },
          );
        return prevMessages;
      });
    };
    return setupSubscription();
  };
  useEffect(() => {
    let _subscriptionObj;
    let _chatSubscriptionObj;
    const setupSubscriptions = async () => {
      _chatSubscriptionObj = await subscribeToPlanner(
        chatId,
        streamingMessageIDRef?.current,
      );
      _subscriptionObj = await subscribeToChatMessages(
        chatId,
        streamingMessageIDRef?.current,
        primaryAgent,
      );
      setChatSubscriptionObject(_chatSubscriptionObj);
      setSubscriptionObject(_subscriptionObj);
      chatSubscriptionObjRef.current = _chatSubscriptionObj;
      subscriptionObjRef.current = _subscriptionObj;
    };
    if (chatId) {
      if (isConnectedToCloud()) setErrorWithSubscription(false);
      if (lastSubscribedChatId == chatId) {
        if (_subscriptionObj) _subscriptionObj?.unsubscribe();
        if (_chatSubscriptionObj) _chatSubscriptionObj?.unsubscribe();
      }
      setLastSubscribedChatId(chatId);
      setAllowStop(false);
      setupSubscriptions();
    }

    return () => {
      if (_subscriptionObj) {
        _subscriptionObj?.unsubscribe();
      }

      if (_chatSubscriptionObj) {
        _chatSubscriptionObj?.unsubscribe();
      }
      subscriptionObjRef.current = null;
      chatSubscriptionObjRef.current = null;
      setIsSubscriptionHealthy(false);
      setIsChatSubscriptionHealthy(false);
    };
  }, [
    chatId,
    lastSubscribedChatId,
    getAgent(chatObject?.primaryRole)?.id,
    isAIStreaming,
    streamingMessageIDRef?.current,
    primaryAgent,
  ]);
  useEffect(() => {
    if (!isConnectedToCloud()) {
      setErrorWithSubscription(true);
    }
  }, [isConnectedToCloud()]);

  // Subscription health check - monitors and restores subscriptions every 5 seconds
  useEffect(() => {
    if (!chatId || !isConnectedToCloud()) {
      return;
    }

    const healthCheckInterval = setInterval(async () => {
      // Check and restore main message subscription
      if (!isSubscriptionHealthy || !subscriptionObjRef.current) {
        envConfig?.REACT_APP_ENV == "dev" &&
          console.log("Restoring main message subscription for chat:", chatId);
        try {
          if (subscriptionObjRef.current) {
            subscriptionObjRef.current.unsubscribe();
          }
          const newSub = await subscribeToChatMessages(
            chatId,
            streamingMessageIDRef?.current,
            primaryAgent,
          );
          subscriptionObjRef.current = newSub;
          setSubscriptionObject(newSub);
          setIsSubscriptionHealthy(true);
          envConfig?.REACT_APP_ENV == "dev" &&
            console.log("Main message subscription restored successfully");
        } catch (error) {
          envConfig?.REACT_APP_ENV == "dev" &&
            console.error(
              "Failed to restore main message subscription:",
              error,
            );
        }
      }

      // Check and restore chat/planner subscription
      if (!isChatSubscriptionHealthy || !chatSubscriptionObjRef.current) {
        envConfig?.REACT_APP_ENV == "dev" &&
          console.log("Restoring chat/planner subscription for chat:", chatId);
        try {
          if (chatSubscriptionObjRef.current) {
            chatSubscriptionObjRef.current.unsubscribe();
          }
          const newSub = await subscribeToPlanner(
            chatId,
            streamingMessageIDRef?.current,
          );
          chatSubscriptionObjRef.current = newSub;
          setChatSubscriptionObject(newSub);
          setIsChatSubscriptionHealthy(true);
          envConfig?.REACT_APP_ENV == "dev" &&
            console.log("Chat/planner subscription restored successfully");
        } catch (error) {
          envConfig?.REACT_APP_ENV == "dev" &&
            console.error(
              "Failed to restore chat/planner subscription:",
              error,
            );
        }
      }

      // Check and restore create message subscription
      if (!isCreateSubscriptionHealthy || !createSubscriptionRef.current) {
        envConfig?.REACT_APP_ENV == "dev" &&
          console.log(
            "Restoring create message subscription for chat:",
            chatId,
          );
        try {
          if (createSubscriptionRef.current) {
            createSubscriptionRef.current.unsubscribe();
          }
          const newSub = await subscribeGraphQL(
            graphqlOperation(onCreateChatMessageByChatIDEvent, {
              chatID: chatId,
            }),
            {
              next: async (data) => {
                setIsCreateSubscriptionHealthy(true);
                const mainMessageObj =
                  data?.value?.data?.onCreateChatMessageByChatIDEvent;
                if (mainMessageObj?.chatID != chatId) return;

                // Check if message already exists (SSE may have created it first)
                const messageAlreadyExists = messagesRef.current?.find(
                  (m) => m._id === mainMessageObj?.id,
                );
                if (messageAlreadyExists) {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log(
                      "[Subscription] onCreate skipped - message already exists (created by SSE):",
                      mainMessageObj?.id,
                    );
                  return;
                }

                // Skip user message creation events when SSE streaming is active
                if (
                  isSSEStreamingRef.current &&
                  mainMessageObj?.role === "user"
                ) {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log(
                      "[Subscription] onCreate skipped - SSE user message:",
                      mainMessageObj?.id,
                    );
                  return;
                }

                lastMessageCreatedRef.current = mainMessageObj?.id;
                const message = mainMessageObj?.text;
                // Determine display name based on role (same logic as adaptMessages)
                const displayName =
                  mainMessageObj?.role === "assistant"
                    ? "assistant"
                    : getUserName(mainMessageObj?.userID) ||
                      mainMessageObj?.role;
                const newMessage = {
                  ...mainMessageObj,
                  _id: mainMessageObj.id,
                  createdAt: mainMessageObj.creationTime,
                  _lastChangedAt: mainMessageObj?._lastChangedAt,
                  user: {
                    id: mainMessageObj?.userID,
                    name: displayName,
                    agentName:
                      primaryAgent?.id != mainMessageObj?.agentID
                        ? getAgent(mainMessageObj?.agentID)?.label
                        : primaryAgent?.label,
                  },
                  text: `${message}`,
                  loading: false,
                  humanFeedback: mainMessageObj?.humanFeedback,
                  humanFeedbackType: mainMessageObj?.humanFeedbackType,
                  humanFeedbackSubType: mainMessageObj?.humanFeedbackSubType,
                  humanFeedbackComment: mainMessageObj?.humanFeedbackComment,
                  metadata:
                    typeof mainMessageObj.metadata === "string"
                      ? JSON.parse(mainMessageObj.metadata)
                      : mainMessageObj.metadata,
                  callbackMetadata:
                    typeof mainMessageObj.callbackMetadata === "string"
                      ? JSON.parse(mainMessageObj.callbackMetadata)
                      : mainMessageObj.callbackMetadata,
                  agentName: getAgent(mainMessageObj?.agentID)?.label,
                };
                setTimeout(() => {
                  setMessages((prevMessages) => {
                    let updatedMessages = [...prevMessages];
                    if (
                      prevMessages?.find((u) => u?._id == newMessage?._id) ==
                      null
                    )
                      updatedMessages.push(newMessage);
                    updatedMessages = updatedMessages.sort((a, b) => {
                      return new Date(a.createdAt) - new Date(b.createdAt);
                    });
                    return [...updatedMessages];
                  });
                }, 1000);
              },
              error: (error) => {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log(`Error with subscription: ${chatId}`, error);
                setIsCreateSubscriptionHealthy(false);
              },
              close: () => {
                setIsSocketConnected(false);
                setIsCreateSubscriptionHealthy(false);
              },
              connect: () => {
                setIsSocketConnected(true);
                setIsCreateSubscriptionHealthy(true);
              },
            },
          );
          createSubscriptionRef.current = newSub;
          setIsCreateSubscriptionHealthy(true);
          envConfig?.REACT_APP_ENV == "dev" &&
            console.log("Create message subscription restored successfully");
        } catch (error) {
          envConfig?.REACT_APP_ENV == "dev" &&
            console.error(
              "Failed to restore create message subscription:",
              error,
            );
        }
      }
    }, 5000); // Run every 5 seconds

    return () => {
      clearInterval(healthCheckInterval);
    };
  }, [
    chatId,
    isSubscriptionHealthy,
    isChatSubscriptionHealthy,
    isCreateSubscriptionHealthy,
    primaryAgent,
    isConnectedToCloud(),
  ]);

  // Chat readiness check - ensures subscriptions are healthy and minimum time has elapsed
  useEffect(() => {
    if (!chatId || isChatReady) return;

    const checkReadiness = () => {
      const allSubscriptionsHealthy =
        isSubscriptionHealthy &&
        isChatSubscriptionHealthy &&
        isCreateSubscriptionHealthy;
      const minTimeElapsed =
        chatOpenedAtRef.current && Date.now() - chatOpenedAtRef.current >= 100;

      if (allSubscriptionsHealthy && minTimeElapsed) {
        setIsChatReady(true);
      }
    };

    // Check immediately
    checkReadiness();

    // Also set up an interval to check periodically until ready
    const readinessInterval = setInterval(checkReadiness, 100);

    return () => {
      clearInterval(readinessInterval);
    };
  }, [
    chatId,
    isChatReady,
    isSubscriptionHealthy,
    isChatSubscriptionHealthy,
    isCreateSubscriptionHealthy,
  ]);

  // Send pending message after chat is created/navigated and ready
  // Uses module-level _pendingMessage to survive component remounts
  useEffect(() => {
    if (_pendingMessage && chatId && isChatReady) {
      const pending = _pendingMessage;
      _pendingMessage = null;

      if (typeof pending === "object" && pending !== null) {
        if (pending.imageKeys) setImageKeys(pending.imageKeys);
        if (pending.audioKeys) setAudioKeys(pending.audioKeys);
        if (pending.videoKey) setVideoKey(pending.videoKey);
        if (pending.fileKeys) setFileKeys(pending.fileKeys);
        if (pending.imageUrls) setImageUrls(pending.imageUrls);
        if (pending.audioUrls) setAudioUrls(pending.audioUrls);
        if (pending.fileUrls) setFileUrls(pending.fileUrls);
        if (pending.videoUrl) setVideoUrl(pending.videoUrl);
        if (pending.imageUrl) setImageUrl(pending.imageUrl);
        if (pending.imageKey) setImageKey(pending.imageKey);
        if (pending.audioUrl) setAudioUrl(pending.audioUrl);
        if (pending.audioKey) setAudioKey(pending.audioKey);
        if (pending.fileUrl) setFileUrl(pending.fileUrl);
        if (pending.fileKey) setFileKey(pending.fileKey);
        if (pending.selectedImageRawKey)
          setSelectedImageRawKey(pending.selectedImageRawKey);
        if (pending.canvasSelection) setCanvasSelection(pending.canvasSelection);
        if (pending.audiosText) setAudiosText(pending.audiosText);
        if (pending.videosText) setVideosText(pending.videosText);
        setPendingSendText(pending.text);
      } else {
        onSendEvent(pending);
      }
    }
  }, [chatId, isChatReady]);

  // Second effect: send the pending message after React has committed the restored attachment state
  useEffect(() => {
    if (pendingSendText) {
      setPendingSendText(null);
      onSendEvent(pendingSendText);
    }
  }, [pendingSendText]);

  useEffect(() => {
    if (transcript?.text) {
      AppQuantumEditorUtils.Utilities.insertTextAtSelection(
        editor,
        transcript.text || "",
      );
      setValue(editor?.children);
    }
  }, [transcript]);
  const saveTopics = async (chatId, payload) => {
    try {
      await chatsHelper.updateChat(chatId, payload);
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" && console.log(error);
      AppToasty.show(t("chats.errorSavingTopics"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  useEffect(() => {
    if (chatId && selectedTopics && isLoading === false) {
      saveTopics(chatId, [
        {
          property: "topics",
          value: selectedTopics ? JSON.stringify(selectedTopics) : null,
        },
      ]);
    }
  }, [selectedTopics?.length]);
  useEffect(() => {
    if (isMobileScreen()) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [isMobileScreen()]);
  useEffect(() => {
    if (homeMode?.isHomeMode) {
      setIsCollapsed(true);
    }
  }, [homeMode?.isHomeMode]);
  const adaptMessages = (_messages) => {
    return _messages.map((message) => {
      // Map userID to actual username for non-assistant messages
      const displayName =
        message.role === "assistant"
          ? message.role
          : getUserName(message.userID) || message.role;
      return {
        _id: message.id,
        // Raw backend discriminator columns (persisted by the planner
        // orchestrator): agentID distinguishes planner narration (== planner
        // id) from sub-agent output (!= planner id); userID is "planner" for
        // both, so it is NOT a discriminator. Kept raw so step attribution
        // and agent-name resolution can run off backend truth, not text.
        agentID: message?.agentID,
        userID: message?.userID,
        user: {
          id: message.userID,
          name: displayName,
          agentName:
            message?.agentID != primaryAgent?.id
              ? getAgent(message?.agentID)?.label
              : null,
        },
        text: message.text,
        createdAt: message.creationTime,
        _lastChangedAt: message?._lastChangedAt,
        status: message.status,
        metadata: message.metadata,
        callbackMetadata: message.callbackMetadata,
        humanFeedback: message.humanFeedback,
        humanFeedbackType: message.humanFeedbackType,
        humanFeedbackSubType: message.humanFeedbackSubType,
        humanFeedbackComment: message.humanFeedbackComment,
        humanFeedbackForInstruction: message.humanFeedbackForInstruction,
        images: message.images,
        audios: message.audios,
        videos: message.videos,
        files: message.files,
        agentName: getAgent(message?.agentID)?.label,
      };
    });
  };
  const loadMessages = async (chatId) => {
    setIsLoading(true);
    let chatObj = getChat(chatId);
    // First, get the chat object to check visibility
    // Check if user has access to this chat based on visibility rules

    const hasAccess =
      !chatObj ||
      chatObj.visibility === "public" ||
      chatObj.visibility === "widget" ||
      (chatObj.visibility === "job" && isUserAIEngineer()) ||
      !chatObj.visibility ||
      (chatObj.visibility === "incognito" && chatObj.createdBy === user?.id) ||
      (chatObj.visibility === "private" &&
        (chatObj.createdBy === user?.id || isUserAIEngineer()));

    setHasAccessToChat(hasAccess);

    if (!hasAccess) {
      // User doesn't have access to this chat
      setMessages([]);
      setStateMessages([]);
      setTimeout(() => {
        setIsLoading(false);
      }, 100);

      return;
    }

    let _messages = await messagesHelper.getChatMessages(chatId);

    _messages = _messages?.data?.messagesByChat?.items?.filter(
      (u) => u?._deleted != true,
    );
    _messages = _messages.map((message) => {
      if (typeof message.metadata === "string") {
        message.metadata = JSON.parse(message.metadata);
      }
      if (typeof message.callbackMetadata === "string") {
        message.callbackMetadata = JSON.parse(message.callbackMetadata);
      }
      return message;
    });
    const sortedMessage = _messages.sort((a, b) => {
      return a.creationTime - b.creationTime;
    });
    setStateMessages(adaptMessages(sortedMessage));

    // Determine correct message limit based on chat's agent mode
    // This fixes the race condition where primaryAgent isn't set yet during initial load
    const chatAgent = chatObj?.primaryRole
      ? getAgent(chatObj.primaryRole)
      : null;
    const isPlannerMode = chatAgent?.mode === "planner";
    const messageLimit = isPlannerMode ? 50 : loadedMessages;

    // Update loadedMessages state to match
    if (isPlannerMode && loadedMessages < 50) {
      setLoadedMessages(50);
    }

    const lastSortedMessages = sortedMessage.slice(-messageLimit);
    setMessages(adaptMessages(lastSortedMessages));
    setTimeout(() => {
      setIsLoading(false);
    }, 100);
    setTimeout(() => {
      setDocumentId(chatObj?.canvasID);
    }, 10);
  };

  /**
   * Fetch latest messages for parallel step execution.
   * This function is called when a step completes during parallel execution.
   * It fetches new messages and merges them into the existing state.
   * Includes debouncing to prevent excessive API calls during rapid parallel completions.
   *
   * @param {string} targetChatId - The chat ID to fetch messages for
   * @param {number} stepId - The step ID that completed (for logging)
   * @param {number} debounceMs - Minimum time between fetches (default 500ms)
   */
  const fetchLatestMessages = useCallback(
    async (targetChatId, stepId, debounceMs = 500) => {
      if (!targetChatId) return;

      const now = Date.now();
      const timeSinceLastFetch = now - lastMessageFetchTimeRef.current;

      // Debounce: if another fetch is pending or we fetched recently, queue this one
      if (timeSinceLastFetch < debounceMs) {
        // Clear any existing pending fetch
        if (pendingMessageFetchRef.current) {
          clearTimeout(pendingMessageFetchRef.current);
        }
        // Schedule a fetch after the debounce period
        pendingMessageFetchRef.current = setTimeout(() => {
          pendingMessageFetchRef.current = null;
          fetchLatestMessages(targetChatId, stepId, debounceMs);
        }, debounceMs - timeSinceLastFetch);
        return;
      }

      lastMessageFetchTimeRef.current = now;

      try {
        envConfig?.REACT_APP_ENV === "dev" &&
          console.log(
            "[fetchLatestMessages] Fetching messages for step:",
            stepId,
          );

        const response = await messagesHelper.getChatMessages(targetChatId);
        let fetchedMessages = response?.data?.messagesByChat?.items?.filter(
          (u) => u?._deleted !== true,
        );

        if (!fetchedMessages || fetchedMessages.length === 0) {
          return;
        }

        // Parse metadata strings
        fetchedMessages = fetchedMessages.map((message) => {
          if (typeof message.metadata === "string") {
            message.metadata = JSON.parse(message.metadata);
          }
          if (typeof message.callbackMetadata === "string") {
            message.callbackMetadata = JSON.parse(message.callbackMetadata);
          }
          return message;
        });

        // Sort by creation time
        const sortedMessages = fetchedMessages.sort(
          (a, b) => a.creationTime - b.creationTime,
        );

        // Get current message IDs for deduplication
        const currentMessageIds = new Set(
          messagesRef.current?.map((m) => m._id) || [],
        );

        // Filter to only new messages
        const newMessages = sortedMessages.filter(
          (msg) => !currentMessageIds.has(msg.id),
        );

        if (newMessages.length > 0) {
          envConfig?.REACT_APP_ENV === "dev" &&
            console.log(
              "[fetchLatestMessages] Adding",
              newMessages.length,
              "new messages",
            );

          // Adapt and merge new messages
          const adaptedNewMessages = adaptMessages(newMessages);

          setMessages((prevMessages) => {
            // Merge and sort by creation time
            const merged = [...prevMessages, ...adaptedNewMessages];
            const deduped = merged.filter(
              (msg, index, self) =>
                index === self.findIndex((m) => m._id === msg._id),
            );
            return deduped.sort((a, b) => a.createdAt - b.createdAt);
          });

          setStateMessages((prevStateMessages) => {
            const merged = [...prevStateMessages, ...adaptedNewMessages];
            const deduped = merged.filter(
              (msg, index, self) =>
                index === self.findIndex((m) => m._id === msg._id),
            );
            return deduped.sort((a, b) => a.createdAt - b.createdAt);
          });
        } else {
          // No new messages, but check for updates to existing messages
          const updatedMessages = sortedMessages.filter((msg) => {
            const existingMsg = messagesRef.current?.find(
              (m) => m._id === msg.id,
            );
            return (
              existingMsg &&
              (existingMsg.text !== msg.text ||
                existingMsg.status !== msg.status)
            );
          });

          if (updatedMessages.length > 0) {
            envConfig?.REACT_APP_ENV === "dev" &&
              console.log(
                "[fetchLatestMessages] Updating",
                updatedMessages.length,
                "existing messages",
              );

            const adaptedUpdates = adaptMessages(updatedMessages);

            setMessages((prevMessages) => {
              return prevMessages.map((msg) => {
                const update = adaptedUpdates.find((u) => u._id === msg._id);
                return update ? { ...msg, ...update } : msg;
              });
            });

            setStateMessages((prevStateMessages) => {
              return prevStateMessages.map((msg) => {
                const update = adaptedUpdates.find((u) => u._id === msg._id);
                return update ? { ...msg, ...update } : msg;
              });
            });
          }
        }
      } catch (error) {
        console.error("[fetchLatestMessages] Error fetching messages:", error);
      }
    },
    [adaptMessages],
  );

  useEffect(() => {
    if (
      chatId &&
      getActiveWorkspace()?.id &&
      chatId !== loadedChatId &&
      state?.agents?.length
    ) {
      setLoadedMessages(MAX_MESSAGES_LOADED);
      setSelectedNode({ id: chatId });
      setLoadedChatId(chatId);
      loadMessages(chatId);
    }
  }, [chatId, getActiveWorkspace()?.id, loadedChatId, state?.agents]);
  // Load workspace functions for resolving agent function IDs
  useEffect(() => {
    const loadWorkspaceFunctions = async () => {
      const workspaceId = getActiveWorkspace()?.id;
      if (!workspaceId || workspaceFunctionsLoadedRef.current === workspaceId)
        return;

      try {
        const result = await functionsHelper.getAgentFunctions(workspaceId);
        const functions = result?.data?.functionsByWorkspace?.items?.filter(
          (fn) => fn?._deleted !== true,
        );
        setWorkspaceFunctions(functions || []);
        workspaceFunctionsLoadedRef.current = workspaceId;
      } catch (error) {
        envConfig?.REACT_APP_ENV == "dev" &&
          console.error("Error loading workspace functions:", error);
      }
    };

    loadWorkspaceFunctions();
  }, [getActiveWorkspace()?.id]);
  // Re-adapt messages when workspaceUsers becomes available (fixes race condition)
  useEffect(() => {
    const workspaceUsers = getWorkspaceUsers();
    if (workspaceUsers?.length > 0 && messages?.length > 0) {
      // Re-adapt messages to update user names now that workspaceUsers is loaded
      setMessages((prevMessages) =>
        prevMessages.map((msg) => {
          if (msg.user?.id && msg.user?.name !== "assistant") {
            const userName = getUserName(msg.user.id);
            if (userName && userName !== msg.user.name) {
              return {
                ...msg,
                user: {
                  ...msg.user,
                  name: userName,
                },
              };
            }
          }
          return msg;
        }),
      );
    }
  }, [getWorkspaceUsers()?.length]);
  const setChatProperties = (chatObj) => {
    if (chatObj?.topics && typeof chatObj?.topics === "string")
      chatObj.topics = JSON.parse(chatObj.topics);
    if (chatObj?.metadata && typeof chatObj?.metadata === "string")
      chatObj.metadata = JSON.parse(chatObj.metadata);
    // For planner mode, don't set isAIStreaming if plan is already completed/failed
    if (primaryAgent?.mode === "planner") {
      setIsAIStreaming(
        chatObj?.isAIReplying === true &&
          chatObj?.metadata?.planning_status !== "plan_execution_completed" &&
          chatObj?.metadata?.planning_status !== "plan_execution_failed",
      );
    } else {
      setIsAIStreaming(chatObj?.isAIReplying === true);
    }
    setActiveStep(chatObj?.metadata?.active_step);
    setPlanningExecution(chatObj?.metadata?.planning_status);
    setTotalSteps(chatObj?.metadata?.plan?.length || 0);
    // Set plan mode properties

    setPlanMode(chatObj?.planMode || false);
    setPlanFile(chatObj?.planFile || null);
    setPlanStatus(chatObj?.planStatus || null);
  };
  const getChatObject = async (chatId) => {
    let chatObj = getChat(chatId);
    if (!chatObj && chatId)
      try {
        let result;
        if (isConnectedToCloud()) result = await chatsHelper.getChat(chatId);
        else {
          result = await DataStore.query(Chat, chatId);
          result = {
            data: {
              getChat: {
                ...result,
              },
            },
          };
        }
        chatObj = result?.data?.getChat;
        setChatProperties(chatObj);
        dispatch("SET_CHATS", [...state?.chats, chatObj]);
      } catch (error) {
        envConfig?.REACT_APP_ENV == "dev" && console.log(error);
        AppToasty.show(t("chats.errorLoadingChat"), {
          placement: "bottom",
          type: "danger",
        });
      }
    else if (chatObj) {
      setChatProperties(chatObj);
    }
    setChatObject(chatObj);
  };
  const lastMessageFromAssistant =
    messages?.filter((u) => u?.user?.name === "assistant")?.slice(-1)[0] || {};
  useEffect(() => {
    if (chatId != loadedChatId || (!chatObject && chatId)) {
      getChatObject(chatId);
    }
  }, [chatId, getActiveWorkspace()?.id, JSON.stringify(chats), isLoading]);
  useEffect(() => {
    if (chatObject?.primaryRole) {
      const agent = getAgent(chatObject?.primaryRole);
      envConfig?.REACT_APP_ENV === "dev" &&
        console.log("[ChatScreen] Agent loaded:", {
          agentId: agent?.id,
          agentLabel: agent?.label,
          allowDeepInternetSearch: agent?.allowDeepInternetSearch,
          allowInternetSearch: agent?.allowInternetSearch,
          source: "state.agents via getAgent()",
        });
      setCanInteractWithChat(
        (chatObject?.visibility !== "widget" &&
          chatObject?.visibility !== "job") ||
          chatObject?.handedOffToHuman,
      );
      setPrimaryAgent(agent);
      if (chatObject?.topics && typeof chatObject?.topics === "string") {
        setSelectedTopics(JSON.parse(chatObject?.topics) || []);
      } else {
        setSelectedTopics(chatObject?.topics || []);
      }
      if (chatObject?.metadata && typeof chatObject?.metadata === "string") {
        setChatMetadata(JSON.parse(chatObject?.metadata) || {});
      } else {
        setChatMetadata(chatObject?.metadata || {});
      }
      // Restore awaiting user input state from chat status
      if (chatObject?.status?.status === "awaitingUserInput") {
        setIsAwaitingUserInput(true);
      } else {
        setIsAwaitingUserInput(false);
      }
    }
    // Sync boundFolderPath state and ref from chat metadata
    const folderPath = chatObject?.metadata?.folderBinding?.path || null;

    setBoundFolderPath(folderPath);
    boundFolderPathRef.current = folderPath;
  }, [JSON.stringify(chatObject), JSON.stringify(state?.agents)]);

  // Resolve agent in home mode even without a chat (for UI customizations)
  useEffect(() => {
    if (
      homeMode?.isHomeMode &&
      homeMode?.agentId &&
      !chatId &&
      state?.agents?.length > 0 &&
      !primaryAgent
    ) {
      const agent = getAgent(homeMode.agentId);
      if (agent && !agent?._deleted) {
        setPrimaryAgent(agent);
      }
    }
  }, [homeMode?.isHomeMode, homeMode?.agentId, chatId, state?.agents?.length, primaryAgent]);

  // ── Live session adoption ─────────────────────────────────────────
  // Lazily bind this chat to a /live session (mixed text/voice mode) once
  // the chat + its agent are known. Memoized per chatId; adoption on the
  // backend is idempotent (an existing active session for the chat is
  // returned with adopted=true, e.g. from another tab).
  useEffect(() => {
    const workspace = getActiveWorkspace();
    const adopt = async () => {
      try {
        const response = await Live.createLiveSession({
          token: workspace?.workspaceToken,
          workspaceid: workspace?.id,
          agentId: chatObject?.primaryRole,
          chatId: chatId,
        });
        if (response?.ok && response?.data?.session_id) {
          liveSessionChatRef.current = chatId;
          setLiveSessionId(response.data.session_id);
        }
      } catch (e) {
        // Live session is an enhancement, never a blocker — silent fail
        envConfig?.REACT_APP_ENV === "dev" &&
          console.log("[ChatScreen] Live session adoption failed:", e?.message);
      }
    };
    if (
      chatId &&
      chatObject?.primaryRole &&
      workspace?.workspaceToken &&
      liveSessionChatRef.current !== chatId
    ) {
      liveSessionChatRef.current = chatId; // mark in-flight to avoid duplicates
      adopt();
    }
    if (!chatId) {
      liveSessionChatRef.current = null;
      setLiveSessionId(null);
    }
  }, [chatId, chatObject?.primaryRole, getActiveWorkspace()?.id]);

  // Refresh the message list after a voice segment ends — catches any
  // transcript messages the AppSync subscription missed.
  const handleVoiceSessionEnded = useCallback(() => {
    if (chatId) {
      fetchLatestMessages(chatId);
    }
  }, [chatId, fetchLatestMessages]);


  useEffect(() => {
    setActiveStep(chatObject?.metadata?.active_step || 0);
    setPlanningExecution(chatObject?.metadata?.planning_status);
    setTotalSteps(chatObject?.metadata?.plan?.length || 0);
  }, [JSON.stringify(chatObject?.metadata)]);
  const goToChat = (chatId) => {
    if (chatId) {
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/${_routeBase}/${chatId}`,
      );
    }
  };
  const goToChatsOverview = () => {
    history.push(`/workspaces/${getActiveWorkspace()?.id}/${_routeBase}`);
  };
  // Handler for switching the primary agent via # mention in chat input (no navigation)
  const handleAgentSelected = async (agent) => {
    if (!agent?.id) return;

    // Home mode: don't switch agent, delegation is handled in handleSendWithAgent
    if (homeMode?.isHomeMode) return;

    try {
      if (chatId) {
        // Existing chat — just update the primaryRole, no navigation
        await chatsHelper.updateChat(chatId, [
          { property: "primaryRole", value: agent.id },
          { property: "plannerAgent", value: agent.id },
        ]);
      }
      setPrimaryAgent(agent);
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.error("Error switching agent:", error);
      AppToasty.show(t("toasts.agentSwitchFailed"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };

  // Bundled agent switch + message send — single atomic operation
  // Handles chat creation if needed, avoids navigation for existing chats
  const handleSendWithAgent = async (agent, message) => {
    if (!agent?.id || !message?.trim()) return;
    try {
      if (!chatId) {
        // No chat yet — create one with the selected agent, then send after ready
        isSendingEventRef.current = true;
        const newChat = await chatsHelper.createChat({
          workspaceID: getActiveWorkspace()?.id,
          allowMultipleRoles: false,
          createdBy: user?.id,
          visibility: getActiveWorkspace()?.defaultChatVisibility || "private",
          primaryRole: agent.id,
          plannerAgent: agent.id,
          name: "New Chat",
          creationTime: Math.floor(new Date().getTime() / 1000),
        });
        const newChatId = newChat?.data?.createChat?.id;
        if (newChatId) {
          setPrimaryAgent(agent);
          _pendingMessage = {
            text: message,
            imageKeys,
            audioKeys,
            videoKey,
            fileKeys,
            imageUrls,
            audioUrls,
            fileUrls,
            videoUrl,
            imageUrl,
            imageKey,
            audioUrl,
            audioKey,
            fileUrl,
            fileKey,
            selectedImageRawKey,
            canvasSelection,
            audiosText,
            videosText,
          };
          history.push(
            `/workspaces/${
              getActiveWorkspace()?.id
            }/${_routeBase}/${newChatId}`,
          );
          setIsCollapsed(true);
          executePostChatCreationActions();
        }
        isSendingEventRef.current = false;
        return;
      }
      // Existing chat — update agent then send, no navigation
      await chatsHelper.updateChat(chatId, [
        { property: "primaryRole", value: agent.id },
        { property: "plannerAgent", value: agent.id },
      ]);
      setPrimaryAgent(agent);
      // Send the message directly — subscriptions are already healthy
      onSendEvent(message);
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.error("Error switching agent and sending:", error);
      AppToasty.show(t("toasts.agentSwitchFailed"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };

  // Folder binding functions
  const handleFolderBound = async (folderData) => {


    if (!chatId) {
      window?.electronAPI?.writeLog?.(
        "warn",
        "folder-bind dropped: no chatId (chat not created yet)",
        { path: folderData?.path, workspaceID: state?.activeWorkspaceID },
      );
      return;
    }

    window?.electronAPI?.writeLog?.("info", "folder-bind start", {
      chatId,
      path: folderData?.path,
    });

    try {
      // Get current chat metadata

      const chat = await chatsHelper.getChat(chatId);
      const currentMetadata = chat?.data?.getChat?.metadata
        ? JSON.parse(chat.data.getChat.metadata)
        : {};



      // Update metadata with folder binding info
      const updatedMetadata = {
        ...currentMetadata,
        folderBinding: {
          path: folderData.path,
          name: folderData.name,
          timestamp: folderData.timestamp || Date.now(),
          boundAt: new Date().toISOString(),
        },
      };



      await chatsHelper.updateChat(chatId, [
        { property: "metadata", value: JSON.stringify(updatedMetadata) },
      ]);

      // Update local chat object state
      setChatObject((prev) => ({
        ...prev,
        metadata: updatedMetadata,
      }));



      AppToasty.show(t("toasts.folderBound", { name: folderData.name }), {
        type: "success",
      });
      window?.electronAPI?.writeLog?.("info", "folder-bind done", {
        chatId,
        path: folderData?.path,
      });
    } catch (error) {
console.error("[LocalFolderAccess] Error binding folder to chat:", {
        chatId,
        folderData,
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
      });
      window?.electronAPI?.writeLog?.("error", "folder-bind failed", {
        chatId,
        path: folderData?.path,
        errorName: error?.name,
        error: error?.message,
      });
      AppToasty.show(t("toasts.folderBindFailed"), {
        type: "danger",
      });
    }
  };

  const handleFolderUnbound = async () => {


    if (!chatId) {

      return;
    }

    try {
      // Get current chat metadata

      const chat = await chatsHelper.getChat(chatId);
      const currentMetadata = chat?.data?.getChat?.metadata
        ? JSON.parse(chat.data.getChat.metadata)
        : {};



      // Remove folder binding info from metadata
      const { folderBinding, ...updatedMetadata } = currentMetadata;



      await chatsHelper.updateChat(chatId, [
        { property: "metadata", value: JSON.stringify(updatedMetadata) },
      ]);

      // Update local chat object state
      setChatObject((prev) => ({
        ...prev,
        metadata: updatedMetadata,
      }));



      AppToasty.show(t("toasts.folderUnbound"), {
        type: "info",
      });
    } catch (error) {
console.error("[LocalFolderAccess] Error unbinding folder from chat:", {
        chatId,
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
      });
      AppToasty.show(t("toasts.folderUnbindFailed"), {
        type: "danger",
      });
    }
  };

  const handleMarkForTraining = async () => {
    if (!chatId) {
      AppToasty.show(t("toasts.noChatSelected"), { type: "warning" });
      return;
    }

    try {
      const chat = await chatsHelper.getChat(chatId);
      const currentMetadata = chat?.data?.getChat?.metadata
        ? JSON.parse(chat.data.getChat.metadata)
        : {};

      const isCurrentlyMarked = currentMetadata?.markedForTraining === true;

      const updatedMetadata = {
        ...currentMetadata,
        markedForTraining: !isCurrentlyMarked,
        markedForTrainingAt: !isCurrentlyMarked
          ? new Date().toISOString()
          : undefined,
      };

      if (isCurrentlyMarked) {
        delete updatedMetadata.markedForTrainingAt;
      }

      await chatsHelper.updateChat(chatId, [
        { property: "metadata", value: JSON.stringify(updatedMetadata) },
      ]);

      setChatObject((prev) => ({
        ...prev,
        metadata: updatedMetadata,
      }));

      AppToasty.show(
        !isCurrentlyMarked
          ? t("chats.ui.markedForTraining")
          : t("chats.ui.unmarkedForTraining"),
        { type: "success" },
      );
    } catch (error) {
      console.error("Error marking chat for training:", error);
      AppToasty.show(t("chats.markTrainingFailed"), { type: "danger" });
    }
  };

  const removeNode = async (node) => {
    setDeletionInProgress(true);
    setShowModal(false);
    setIsLoading(true);
    if (selectedNode?.id === selectedNodeToDelete.id) {
      try {
        result = await chatsHelper.deleteChat(selectedNode?.id);
        const indexOfDeletedNode =
          state?.treeViewData
            ?.filter((u) => !u.data?.droppable)
            .map((u) => u.id)
            ?.indexOf(selectedNodeToDelete.id) - 1;
        AppToasty.show(t("chats.chatDeleted"), {
          placement: "bottom",
          type: "success",
        });
        setChats(chats.filter((u) => u.id?.toString() != node?.id));
      } catch (error) {
        envConfig?.REACT_APP_ENV == "dev" && console.error(error);
        AppToasty.show(t("chats.errorDeletingChat"), {
          placement: "bottom",
          type: "danger",
        });
      } finally {
        goToChatsOverview();
        setDeletionInProgress(false);
        setIsLoading(false);
      }
    }
  };

  const handleClickOnNode = (node, toDelete = false) => {
    if (toDelete) setSelectedNodeToDelete(node);
    else {
      setSelectedNode(node);
      if (
        node &&
        node?.id !== selectedNode?.id &&
        node?.data?.droppable != true
      ) {
        // Check if AI is streaming and warn user before navigating
        if (isAgentResponding) {
          const warningMessage =
            "An agent is currently responding. If you leave now, the response may be lost. Do you want to continue?";
          const confirmed = window.confirm(warningMessage);
          if (!confirmed) {
            return;
          }
          // User confirmed, cancel the stream before navigating
          if (isSSEStreaming) {
            cancelSSEStream();
          }
          setIsAIStreaming(false);
        }
        setIsLoading(true);
        goToChat(node?.id);
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
      }
    }
  };

  const setDialogActionOnTree = useCallback(
    (action) => {
      setShowModal(true);
      setTreeViewActionContext(action);
    },
    [setShowModal, setTreeViewActionContext],
  );
  const onMove = (object) => {
    envConfig?.REACT_APP_ENV == "dev" && console.log("onMove", object);
  };
  const executePostChatCreationActions = () => {
    setIsLoading(false);
    setCreationOfNewChatInProgress(false);
    setSelectedTopics([]);
    loadChats(false, true);
    setShowWidgetChatsHistory(false);
  };
  const params = useParams();
  useEffect(() => {
    if (
      params?.chatId &&
      params?.chatId?.includes("newchat") &&
      getActiveWorkspace()?.id &&
      user?.id
    ) {
      const _agent_id = params?.chatId?.split("_")[1];
      handleParentActionClick(_agent_id);
    }
  }, [
    params?.chatId,
    state?.agents?.length,
    getActiveWorkspace()?.id,
    user?.id,
  ]);

  // In home mode, do NOT auto-create a chat — show the greeting screen instead.
  // A new chat will be created when the user sends their first message.

  const handleParentActionClick = async (agentid) => {
    // Check if AI is streaming and warn user before creating new chat
    if (isAgentResponding) {
      const warningMessage =
        "An agent is currently responding. If you leave now, the response may be lost. Do you want to continue?";
      const confirmed = window.confirm(warningMessage);
      if (!confirmed) {
        return;
      }
      // User confirmed, cancel the stream before navigating
      if (isSSEStreaming) {
        cancelSSEStream();
      }
      setIsAIStreaming(false);
    }
    if (isConnectedToCloud())
      result = await chatsHelper.createChat({
        workspaceID: getActiveWorkspace()?.id,
        allowMultipleRoles: false,
        createdBy: user?.id,
        visibility: getActiveWorkspace()?.defaultChatVisibility || "private",
        primaryRole: agentid,
        plannerAgent: agentid,
        name: "New Chat",
        creationTime: Math.floor(new Date().getTime() / 1000),
      });
    else {
      result = await DataStore.save(
        new Chat({
          workspaceID: getActiveWorkspace()?.id,
          allowMultipleRoles: false,
          createdBy: user?.id,
          visibility: getActiveWorkspace()?.defaultChatVisibility || "private",
          primaryRole: agentid,
          plannerAgent: agentid,
          name: "New Chat (Local)",
          creationTime: Math.floor(new Date().getTime() / 1000),
        }),
      );
      result = {
        data: {
          createChat: result,
        },
      };
    }
    history.push(
      `/workspaces/${getActiveWorkspace()?.id}/${_routeBase}/${
        result?.data?.createChat?.id
      }`,
    );

    setIsCollapsed(true);
    executePostChatCreationActions();
  };
  const createChat = async (selectedNode, values, isEdit) => {
    // Check if AI is streaming and warn user before creating/editing chat (only for new chats that navigate)
    if (!isEdit && isAgentResponding) {
      const warningMessage =
        "An agent is currently responding. If you leave now, the response may be lost. Do you want to continue?";
      const confirmed = window.confirm(warningMessage);
      if (!confirmed) {
        return;
      }
      // User confirmed, cancel the stream before navigating
      if (isSSEStreaming) {
        cancelSSEStream();
      }
      setIsAIStreaming(false);
    }
    try {
      setIsLoading(true);
      setCreationOfNewChatInProgress(true);
      let channelSettings = null;
      let externalParticipantId = null;
      if (values?.toPhoneNumber || values?.toEmail) {
        if (values?.channelType == "email")
          externalParticipantId = values?.toEmail;
        else if (
          values?.channelType == "sms" ||
          values?.channelType == "whatsapp"
        )
          externalParticipantId = values?.toPhoneNumber;
        channelSettings = {
          sms: {
            isEnabled: values?.channelType == "sms",
            recipient: values?.toPhoneNumber,
            providerID:
              values?.channelType == "sms" ? values?.providerId : null,
          },
          whatsapp: {
            isEnabled: values?.channelType == "whatsapp",
            recipient: values?.toPhoneNumber,
            providerID:
              values?.channelType == "whatsapp" ? values?.providerId : null,
          },
          email: {
            isEnabled: values?.channelType == "email",
            recipient: values?.toEmail,
            providerID:
              values?.channelType == "email" ? values?.providerId : null,
          },
        };
      }
      delete values.toPhoneNumber;
      delete values.toEmail;
      delete values.channelType;
      delete values.providerId;
      // CREATE CHAT
      if (isEdit) {
        result = await chatsHelper.updateChat(selectedNode?.id, [
          { property: "name", value: values.name },
          { property: "description", value: values.description },
          { property: "primaryRole", value: values.primaryRole },
          { property: "plannerAgent", value: values.primaryRole },
          {
            property: "visibility",
            value: values.visibility || "private",
          },
          {
            property: "channelSettings",
            value: JSON.stringify(channelSettings),
          },
          {
            property: "externalParticipantId",
            value: externalParticipantId,
          },
        ]);

        AppToasty.show(t("chats.chatUpdateCompleted"), {
          placement: "bottom",
          type: "success",
        });
      } else {
        const _visibility = values.visibility || "private";
        delete values.visibility;
        result = await chatsHelper.createChat({
          ...values,
          plannerAgent: values.primaryRole,
          workspaceID: getActiveWorkspace()?.id,
          allowMultipleRoles: false,
          createdBy: user?.id,
          visibility: _visibility,
          creationTime: Math.floor(new Date().getTime() / 1000),
          channelSettings: JSON.stringify(channelSettings),
          externalParticipantId: externalParticipantId,
        });
        history.push(
          `/workspaces/${getActiveWorkspace()?.id}/${_routeBase}/${
            result?.data?.createChat?.id
          }`,
        );
      }
      setShowModal(false);
      setIsLoading(false);
      setCreationOfNewChatInProgress(false);
      setSelectedTopics([]);
      loadChats(false, true);
      setShowWidgetChatsHistory(false);
      setIsCollapsed(true);
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.error("Error creating chat", error);
      AppToasty.show(t("chats.chatCreationError"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  const updatePlanApprovalState = async (id) => {
    try {
      // console.log("Updating plan approval state for ID:", id);
      let metadata = messages?.find((msg) => msg.id === id)?.metadata;
      if (typeof metadata == "string") metadata = safeJsonParse(metadata);
      await messagesHelper.updateChatMessage(id, [
        {
          property: "metadata",
          value: JSON.stringify({
            ...metadata,
            quickApprove: false,
          }),
        },
      ]);
      const indexMessage = messages
        ?.map((u) => u.id)
        ?.indexOf((msg) => msg === id);
      if (indexMessage > -1) {
        let _messages = [...messages];
        _messages[indexMessage].metadata = metadata;
        setMessages(_messages);
      }
      setPlanningExecution("planning_approved");
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.error("Error updating chat message", error);
      AppToasty.show(t("chats.chatApprovalError"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  const onLoadEarlier = async () => {
    let newLoadedMessages = loadedMessages + MAX_MESSAGES_LOADED;
    setMessages(stateMessages.slice(-newLoadedMessages));
    setLoadedMessages(newLoadedMessages);
  };
  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function startStreaming(
    message,
    primaryAgent,
    assistantMessageId,
    imageBase64Array = [],
  ) {
    const controller = new AbortController();
    const signal = controller.signal;
    let existingMessages = [];
    if (messagesRef?.current?.length > primaryAgent?.maxHistory || 10)
      existingMessages = messagesRef?.current.slice(-primaryAgent?.maxHistory);
    else existingMessages = messagesRef?.current || [];

    existingMessages = existingMessages?.map((u) => {
      return {
        role: u?.user?.id == null ? "assistant" : "user",
        content: u?.text,
      };
    });

    // Build user message - include images if present (for Ollama vision models)
    const userMessage = {
      role: "user",
      content: message,
    };
    if (imageBase64Array.length > 0) {
      userMessage.images = imageBase64Array;
    }

    fetch(`${agentHosting?.endpoint}/api/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify({
        model: primaryAgent?.llmBaseModel,
        messages: [
          { role: "system", content: generateSystemMessage(primaryAgent) },
          ...existingMessages,
          userMessage,
        ],
        options: {
          temperature: primaryAgent?.temperature || 0.7,
          num_ctx: primaryAgent?.maxTokens || 8192,
          stop: ["</s>"],
        },
        // format: "json",
      }),
      signal: signal,
    })
      .then((response) => {
        handleStreamResponse(response, controller, assistantMessageId);
      })
      .catch(handleStreamError);
    return controller; // Return the controller for external abort access
  }
  function handleStreamError(err) {
    if (err.name === "AbortError") {
      envConfig?.REACT_APP_ENV == "dev" && console.log("Fetch aborted");
    } else {
      envConfig?.REACT_APP_ENV == "dev" && console.error("Fetch error:", err);
    }
    setIsAIStreaming(false);
    setAllowStop(false);
  }
  const getMessages = () => {
    return messagesRef.current;
  };
  const updateMessagesWhileStreamingOffline = async (
    text,
    assistantMessageId,
    getMessages,
  ) => {
    const messages = getMessages();
    const assistaMessageIndex = messages
      ?.map((u) => u?._id)
      ?.indexOf(assistantMessageId);
    if (assistaMessageIndex === -1) return;

    // Skip updates for sealed messages (status === "continued")
    if (messages[assistaMessageIndex]?.status === "continued") {
      return;
    }

    let newMessages = [...messages];
    newMessages[assistaMessageIndex] = {
      ...newMessages[assistaMessageIndex],
      text,
      loading: false,
    };
    setMessages(newMessages);
  };
  // Helper function to update messages while SSE streaming
  const updateMessagesWhileSSEStreaming = useCallback(
    (chunk, messageId, accumulatedText) => {
      setMessages((prevMessages) => {
        const messageIndex = prevMessages.findIndex((m) => m._id === messageId);
        if (messageIndex === -1) return prevMessages;

        const updatedMessages = [...prevMessages];
        const currentMessage = updatedMessages[messageIndex];

        // Skip text updates for sealed messages (status === "continued")
        if (currentMessage.status === "continued") {
          return prevMessages;
        }

        updatedMessages[messageIndex] = {
          ...currentMessage,
          text:
            accumulatedText + (isSSEStreamingRef.current ? " " + cursor : ""),
          loading: false,
        };

        return updatedMessages;
      });
    },
    [cursor],
  );
  // Helper function to update message metadata and callbackMetadata during SSE streaming
  const updateMessageMetadataSSE = useCallback(
    (messageId, metadata, callbackMetadata) => {
      setMessages((prevMessages) => {
        const messageIndex = prevMessages.findIndex((m) => m._id === messageId);
        if (messageIndex === -1) {
          envConfig?.REACT_APP_ENV == "dev" &&
            console.log(
              "[SSE] updateMessageMetadataSSE: message not found",
              messageId,
            );
          return prevMessages;
        }

        const updatedMessages = [...prevMessages];
        const currentMessage = updatedMessages[messageIndex];

        // Build updates object with only defined values. MERGE metadata rather than
        // replace so backend-authoritative fields (step_id, from_planner,
        // planner_execution_plan) are preserved when a partial SSE update
        // omits them — see mergePlannerMetadata.
        const updates = {};
        if (metadata !== undefined) {
          const incoming =
            typeof metadata === "string" ? safeJsonParse(metadata) : metadata;
          updates.metadata = mergePlannerMetadata(
            currentMessage.metadata,
            incoming,
          );
        }
        if (callbackMetadata !== undefined) {
          updates.callbackMetadata =
            typeof callbackMetadata === "string"
              ? safeJsonParse(callbackMetadata)
              : callbackMetadata;
        }

        // Skip update if no changes
        if (Object.keys(updates).length === 0) return prevMessages;

        // Log agent_processing_status state update
        if (updates.metadata?.agent_processing_status) {
          envConfig?.REACT_APP_ENV == "dev" &&
            console.log(
              "[SSE] updateMessageMetadataSSE: applying agent_processing_status",
              {
                messageId,
                agent_processing_status:
                  updates.metadata.agent_processing_status,
                previousStatus:
                  currentMessage.metadata?.agent_processing_status,
              },
            );
        }

        updatedMessages[messageIndex] = {
          ...currentMessage,
          ...updates,
        };

        return updatedMessages;
      });
    },
    [],
  );
  function parseJSONSafely(str) {
    try {
      // First, try parsing the string as-is
      return JSON.parse(str);
    } catch (e) {
      // If that fails, try to escape special characters
      try {
        const escaped = str.replace(/[\n\r\t]/g, (match) => {
          const escapeMap = {
            "\n": "\\n",
            "\r": "\\r",
            "\t": "\\t",
          };
          return escapeMap[match] || match;
        });
        return JSON.parse(escaped);
      } catch (e2) {
        // If all else fails, throw an error
        throw new Error("Unable to parse JSON: " + e2.message);
      }
    }
  }
  function handleStreamResponse(response, controller, assistantMessageId) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = "";
    function processStream(assistantMessageId) {
      reader
        .read()
        .then(async ({ done, value }) => {
          if (done) {
            await messagesHelper.updateChatMessage(assistantMessageId, [
              {
                property: "text",
                value: fullText,
              },
            ]);
            setIsAIStreaming(false);
            setAllowStop(true);
            return;
          }

          const chunk = decoder.decode(value);
          try {
            // console.log("Chunk", parseJSONSafely(chunk)?.message?.content);
            fullText += parseJSONSafely(chunk)?.message?.content;
          } catch (ex) {
            const _jsonArray = chunk?.split("\n");
            if (_jsonArray?.length > 0) {
              _jsonArray?.map((u) => {
                try {
                  const _json_chunk = JSON.parse(u);
                  fullText += _json_chunk?.message?.content;
                } catch (error) {}
              });
            }
          }
          updateMessagesWhileStreamingOffline(
            fullText,
            assistantMessageId,
            getMessages,
          );

          processStream(assistantMessageId); // Continue reading
        })
        .catch(async (err) => {
          if (err.name === "AbortError") {
            envConfig?.REACT_APP_ENV == "dev" && console.log("Stream aborted");
          } else {
            envConfig?.REACT_APP_ENV == "dev" &&
              console.error("Stream error:", err);
          }
          await messagesHelper.updateChatMessage(assistantMessageId, [
            {
              property: "text",
              value: fullText,
            },
          ]);
        });
    }
    processStream(assistantMessageId);
  }

  // Helper function to wait for chat readiness before sending
  const waitForChatReady = async () => {
    // Maximum 5 seconds wait
    const startTime = Date.now();

    while (!isChatReady && Date.now() - startTime < maxWaitTime) {
      await wait(100);
    }

    // If still not ready after max wait, check if minimum conditions are met
    if (!isChatReady) {
      const minTimeElapsed =
        chatOpenedAtRef.current &&
        Date.now() - chatOpenedAtRef.current >= maxWaitTime;
      if (!minTimeElapsed) {
        const remainingTime =
          maxWaitTime - (Date.now() - chatOpenedAtRef.current);
        if (remainingTime > 0) {
          await wait(remainingTime);
        }
      }
    }
  };

  const onSendEvent = async (message) => {
    try {
      if (isSendingEventRef?.current == true) return;
      if (message.trim().length === 0) return;

      // In home mode with no chat, create one first then navigate
      if (homeMode?.isHomeMode && !chatId) {
        isSendingEventRef.current = true;
        const agentid = homeMode.agentId;
        const newChat = await chatsHelper.createChat({
          workspaceID: getActiveWorkspace()?.id,
          allowMultipleRoles: false,
          createdBy: user?.id,
          visibility: getActiveWorkspace()?.defaultChatVisibility || "private",
          primaryRole: agentid,
          plannerAgent: agentid,
          name: "New Chat",
          creationTime: Math.floor(new Date().getTime() / 1000),
        });
        const newChatId = newChat?.data?.createChat?.id;
        if (newChatId) {
          _pendingMessage = {
            text: message,
            imageKeys,
            audioKeys,
            videoKey,
            fileKeys,
            imageUrls,
            audioUrls,
            fileUrls,
            videoUrl,
            imageUrl,
            imageKey,
            audioUrl,
            audioKey,
            fileUrl,
            fileKey,
            selectedImageRawKey,
            canvasSelection,
            audiosText,
            videosText,
          };
          history.push(
            `/workspaces/${
              getActiveWorkspace()?.id
            }/${_routeBase}/${newChatId}`,
          );
          setIsCollapsed(true);
        }
        isSendingEventRef.current = false;
        return;
      }

      // Wait for chat to be ready (subscriptions healthy + minimum time elapsed)
      await waitForChatReady();

      if (agentHosting?.type == "ollama" && !isOllamaRunning()) {
        AppToasty.show(t("chats.ollamaNotRunning"), {
          placement: "bottom",
          type: "danger",
        });
        return;
      }
      isSendingEventRef.current = true;
      // Reset awaiting user input state when user sends a message
      setIsAwaitingUserInput(false);
      if (agentHosting?.type == "ollama") {
        setAllowStop(true);
        setIsAIStreaming(true);

        // OLLAMA ONLY: Convert images to base64 for vision models
        let imageBase64Array = [];
        if (imageUrls?.length > 0) {
          try {
            imageBase64Array = await imageUrlsToBase64Array(imageUrls);
          } catch (error) {
            envConfig?.REACT_APP_ENV == "dev" &&
              console.error("Error converting images to base64:", error);
          }
        }

        // OLLAMA ONLY: Extract text from files and append to message
        let messageWithFiles = message;
        if (fileUrls?.length > 0 && fileKeys?.length > 0) {
          try {
            const fileContents = await Promise.all(
              fileUrls.map(async (url, index) => {
                const fileName = fileKeys[index]?.split("/").pop() || "file";
                const response = await fetch(url);
                const blob = await response.blob();
                const text = await extractTextFromFile(blob, fileName);
                return { name: fileName, content: text };
              }),
            );

            const filesContext = fileContents
              .filter((f) => f.content && f.content.length > 0)
              .map((f) => `--- ${f.name} ---\n${f.content}`)
              .join("\n\n");

            if (filesContext) {
              messageWithFiles = `${message}\n\n${filesContext}`;
            }
          } catch (error) {
            // Show error for unsupported file types (DOCX, etc.)
            AppToasty.show(error.message, {
              placement: "bottom",
              type: "danger",
            });
            setIsAIStreaming(false);
            setAllowStop(false);
            isSendingEventRef.current = false;
            return;
          }
        }

        const promises = [
          messagesHelper.createChatMessage({
            chatID: selectedNode?.id,
            workspaceID: getActiveWorkspace()?.id,
            text: message,
            role: user?.username,
            userID: user?.id,
            isAI: false,
            creationTime: Math.floor(new Date().getTime() / 1000),
            images: imageKeys ? imageKeys : null,
            audios: audioKeys ? audioKeys : null,
            videos: videoKey ? [videoKey] : null,
            files: fileKeys ? fileKeys : null,
            // audiosText: audiosText,
            // videosText: videosText,
          }),
          messagesHelper.createChatMessage({
            chatID: selectedNode?.id,
            workspaceID: getActiveWorkspace()?.id,
            text: "**...**",
            role: "assistant",
            isAI: true,
            creationTime: Math.floor(new Date().getTime() / 1000) + 5,
            agentID: primaryAgent?.id,
          }),
        ];
        const results = await Promise.all(promises);
        let newMessage = results[0]?.data?.createChatMessage;
        let assistantPlaceholder = results[1]?.data?.createChatMessage;
        setMessages((previousMessages) =>
          generateUniqueMessages([
            ...previousMessages,
            {
              _id: newMessage?.id,
              text: message,
              createdAt: Math.floor(new Date().getTime() / 1000),
              user: { id: user?.id, name: user?.username, agentName: null },
              loading: false,
              images: imageKeys ? imageKeys : null,
              audios: audioKeys ? audioKeys : null,
              videos: videoKey ? [videoKey] : null,
              files: fileKeys ? fileKeys : null,
            },
            {
              _id: assistantPlaceholder?.id,
              text: null,
              createdAt: Math.floor(new Date().getTime() / 1000) + 5,
              user: {
                id: null,
                name: "assistant",
                parentMessageId: newMessage?.id,
                agentName: primaryAgent?.label,
              },
              loading: true,
            },
          ]),
        );
        setStreamingMessageID(assistantPlaceholder?.id);
        localStreamingController.current = startStreaming(
          messageWithFiles,
          primaryAgent,
          assistantPlaceholder?.id,
          imageBase64Array,
        );
        return;
      }
      let promises = [];

      // Check if AI is currently streaming in planner, agenticRAG, or useInterleavedReasoning mode
      // If so, create a pending message instead of cancelling the stream
      // Allow typing during post-task cleanup phase (generating tables, mermaid charts, summaries)
      // For non-planner agents, queue behavior must be 100% consistent with ChatWebInput's Send icon:
      // - Send icon visible (isStreaming=false) → send immediately, don't queue
      // - Send icon hidden (isStreaming=true) → queue the message
      const isEffectivelyStreamingForQueue =
        primaryAgent?.mode === "planner"
          ? isSSEStreaming || isAIStreaming
          : isAIStreaming && chatObject?.forcePlannerStop !== true;
      if (
        (primaryAgent?.mode === "planner" ||
          primaryAgent?.agenticRAG === true ||
          primaryAgent?.useInterleavedReasoning === true) &&
        isEffectivelyStreamingForQueue &&
        !isPostTaskCleanupPhase()
      ) {
        // Planner/Computer mode streaming - create pending message
        const pendingMessageResult = await messagesHelper.createChatMessage({
          chatID: chatIdRef.current,
          workspaceID: getActiveWorkspace()?.id,
          text: message,
          role: user?.username,
          userID: user?.id,
          isAI: false,
          creationTime: Math.floor(new Date().getTime() / 1000),
          images: imageKeys ? imageKeys : null,
          audios: audioKeys ? audioKeys : null,
          videos: videoKey ? [videoKey] : null,
          files: fileKeys ? fileKeys : null,
          status: "pending",
        });

        const pendingMessage = pendingMessageResult?.data?.createChatMessage;
        setMessages((previousMessages) =>
          generateUniqueMessages([
            ...previousMessages,
            {
              _id: pendingMessage?.id,
              text: message,
              createdAt: Math.floor(new Date().getTime() / 1000),
              _lastChangedAt: pendingMessage?._lastChangedAt,
              user: { id: user?.id, name: user?.username, agentName: null },
              loading: false,
              status: "pending",
              images: imageKeys ? imageKeys : null,
              audios: audioKeys ? audioKeys : null,
              videos: videoKey ? [videoKey] : null,
              files: fileKeys ? fileKeys : null,
            },
          ]),
        );

        isSendingEventRef.current = false;

        // Inform user message is queued
        AppToasty.show(t("toasts.queued"), {
          placement: "bottom",
          type: "info",
        });
        return;
      }

      if (isAIStreaming && streamingMessageID) {
        subscriptionObj?.unsubscribe();
        await messagesHelper.updateChatMessage(streamingMessageID, [
          {
            property: "status",
            value: "cancelled",
          },
        ]);
        setIsAIStreaming(false);
        setLastUpdateMessageTime(null);
        await wait(1000);
      } else if (isSubscriptionRequired)
        setSubscriptionObject(
          subscribeToChatMessages(chatId, streamingMessageID),
        );
      if (chatObject?.handedOffToHuman) {
        promises = [
          messagesHelper.createChatMessage({
            chatID: chatIdRef.current,
            workspaceID: getActiveWorkspace()?.id,
            text: message,
            role: "assistant",
            userID: user?.id,
            isAI: false,
            creationTime: Math.floor(new Date().getTime() / 1000),
            images: imageKeys ? imageKeys : null,
            audios: audioKeys ? audioKeys : null,
            videos: videoKey ? [videoKey] : null,
            files: fileKeys ? fileKeys : null,
            // audiosText: audiosText,
            // videosText: videosText,
          }),
          chatsHelper.updateChat(chatId, [
            {
              property: "isAIReplying",
              value: false,
            },
            {
              property: "forcePlannerStop",
              value: false,
            },
          ]),
        ];
        const results = await Promise.all(promises);
        let newMessage = results[0]?.data?.createChatMessage;
        setMessages((previousMessages) =>
          generateUniqueMessages([
            ...previousMessages,
            {
              _id: newMessage?.id,
              text: message,
              createdAt: Math.floor(new Date().getTime() / 1000),
              _lastChangedAt: newMessage?._lastChangedAt,
              user: {
                id: user?.id,
                name: "assistant",
                agentName: null,
                isUser: true,
              },
              loading: false,
              images: imageKeys ? imageKeys : null,
              audios: audioKeys ? audioKeys : null,
              videos: videoKey ? [videoKey] : null,
              files: fileKeys ? fileKeys : null,
            },
          ]),
        );
        isSendingEventRef.current = false;
      } else {
        // Use SSE streaming for chatter mode, GraphQL subscription for planner mode
        if (primaryAgent?.mode !== "planner") {
          // SSE streaming approach for chatter mode
          // Backend creates BOTH user and assistant messages - don't create here to avoid duplication
          // Add optimistic user message to UI
          const optimisticUserMessageId = `optimistic_${Date.now()}`;
          setMessages((previousMessages) =>
            generateUniqueMessages([
              ...previousMessages,
              {
                _id: optimisticUserMessageId,
                text: message,
                createdAt: Math.floor(new Date().getTime() / 1000),
                _lastChangedAt: Date.now(),
                user: { id: user?.id, name: user?.username, agentName: null },
                loading: false,
                images: imageKeys ? imageKeys : null,
                audios: audioKeys ? audioKeys : null,
                videos: videoKey ? [videoKey] : null,
                files: fileKeys ? fileKeys : null,
              },
            ]),
          );

          setAllowStop(true);
          setIsAIStreaming(true);
          isSendingEventRef.current = false;
          setWaitingForFirstResponse(true);
          setWaitingForFirstChunk(false);

          let assistantMessageId = null;

          startSSEStream(
            {
              chatid: chatIdRef.current,
              workspaceid: getActiveWorkspace()?.id,
              token: getActiveWorkspace()?.workspaceToken,
              messages: [
                {
                  text: message,
                  role: "user",
                  userID: user?.id,
                  images: imageKeys ? imageKeys : null,
                  audios: audioKeys ? audioKeys : null,
                  videos: videoKey ? [videoKey] : null,
                  files: fileKeys ? fileKeys : null,
                },
              ],
              agentid: primaryAgent?.id || homeMode?.agentId,
              baseImage: selectedImageRawKey,
              canvasSelection: canvasSelection
                ? JSON.stringify(canvasSelection)
                : null,
              // Include bound folder path for local folder access tool
              boundFolderPath: boundFolderPathRef.current,
            },
            {
              onToken: (chunk, messageId, accumulatedText) => {
                console.log("[SSE_DEBUG] onToken:", {
                  chunkLength: chunk?.length,
                  messageId,
                  accumulatedLength: accumulatedText?.length,
                  timestamp: new Date().toISOString(),
                });
                updateMessagesWhileSSEStreaming(
                  chunk,
                  messageId || assistantMessageId,
                  accumulatedText,
                );
              },
              onMessage: (data) => {
                console.log("[SSE_DEBUG] onMessage:", {
                  type: data.type,
                  message_id: data.message_id,
                  status: data.status,
                  hasMetadata: !!data.metadata,
                  hasText: !!data.text,
                  textLength: data.text?.length,
                  timestamp: new Date().toISOString(),
                });
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log("[SSE] onMessage:", data);

                // Handle canvas_required event — agent requests canvas creation
                if (data.type === "canvas_required") {
                  console.log(
                    "[Canvas] Agent requested canvas creation via SSE",
                  );
                  handleCanvasCreation(true);
                  return;
                }

                // Handle chat_created event - backend created the assistant message
                if (data.type === "chat_created" && data.message_id) {
                  assistantMessageId = data.message_id;
                  setStreamingMessageID(data.message_id);
                  // Got first response with message_id, now waiting for first chunk
                  setWaitingForFirstResponse(false);
                  setWaitingForFirstChunk(true);
                  // Add assistant message placeholder to UI
                  setMessages((previousMessages) => {
                    // Check if message already exists
                    if (
                      previousMessages.find((m) => m._id === data.message_id)
                    ) {
                      return previousMessages;
                    }
                    return generateUniqueMessages([
                      ...previousMessages,
                      {
                        _id: data.message_id,
                        text: null,
                        createdAt: Math.floor(new Date().getTime() / 1000) + 1,
                        _lastChangedAt: Date.now(),
                        user: {
                          id: null,
                          name: "assistant",
                          parentMessageId: optimisticUserMessageId,
                          agentName: primaryAgent?.label,
                        },
                        loading: true,
                        // Include files/media if present in chat_created event
                        files: data.files || [],
                        images: data.images || [],
                        audios: data.audios || [],
                        videos: data.videos || [],
                      },
                    ]);
                  });
                }

                // Handle message_sealed event - continuation message created during interleaved reasoning
                // Backend splits message after task completion, frontend must update streamingMessageID
                if (
                  data.type === "message_sealed" &&
                  data.continuation_message_id
                ) {
                  console.log("[SSE_DEBUG] message_sealed:", {
                    old_message_id: data.message_id,
                    new_message_id: data.continuation_message_id,
                    status: data.status,
                    timestamp: new Date().toISOString(),
                  });
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log("[SSE] message_sealed received:", {
                      old_message_id: data.message_id,
                      new_message_id: data.continuation_message_id,
                      status: data.status,
                    });

                  // Update streaming message ID to the new continuation message
                  assistantMessageId = data.continuation_message_id;
                  setStreamingMessageID(data.continuation_message_id);

                  // Reset accumulated text so the continuation message starts fresh.
                  // Without this, accumulatedTextRef carries ALL previous text,
                  // causing each continuation to display the full cumulative content.
                  resetAccumulatedText();

                  // Add the continuation message placeholder to UI
                  setMessages((previousMessages) => {
                    // Check if continuation message already exists
                    if (
                      previousMessages.find(
                        (m) => m._id === data.continuation_message_id,
                      )
                    ) {
                      return previousMessages;
                    }

                    // Find the sealed message to get its metadata
                    const sealedMessage = previousMessages.find(
                      (m) => m._id === data.message_id,
                    );

                    return generateUniqueMessages([
                      ...previousMessages,
                      {
                        _id: data.continuation_message_id,
                        text: null,
                        createdAt: Math.floor(new Date().getTime() / 1000) + 1,
                        _lastChangedAt: Date.now(),
                        user: {
                          id: null,
                          name: "assistant",
                          parentMessageId: sealedMessage?.user?.parentMessageId,
                          agentName: sealedMessage?.user?.agentName,
                        },
                        loading: true,
                        // Carry forward files/media from sealed message
                        files: sealedMessage?.files || [],
                        images: sealedMessage?.images || [],
                        audios: sealedMessage?.audios || [],
                        videos: sealedMessage?.videos || [],
                        metadata: sealedMessage?.metadata,
                      },
                    ]);
                  });

                  // Mark the sealed message as completed (not loading anymore)
                  setMessages((prevMessages) => {
                    const messageIndex = prevMessages.findIndex(
                      (m) => m._id === data.message_id,
                    );
                    if (messageIndex === -1) return prevMessages;

                    const updatedMessages = [...prevMessages];
                    updatedMessages[messageIndex] = {
                      ...updatedMessages[messageIndex],
                      loading: false,
                      status: data.status || "continued",
                    };
                    return updatedMessages;
                  });
                }

                // Handle files, images, audios, videos updates from SSE
                // These are sent as top-level properties on "message" type events
                if (
                  (data.files || data.images || data.audios || data.videos) &&
                  data.message_id
                ) {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log("[SSE] File/media update received:", {
                      message_id: data.message_id,
                      files: data.files,
                      images: data.images,
                      audios: data.audios,
                      videos: data.videos,
                    });

                  setMessages((prevMessages) => {
                    const messageIndex = prevMessages.findIndex(
                      (m) => m._id === data.message_id,
                    );
                    if (messageIndex === -1) return prevMessages;

                    const updatedMessages = [...prevMessages];
                    updatedMessages[messageIndex] = {
                      ...updatedMessages[messageIndex],
                      files: data.files || updatedMessages[messageIndex].files,
                      images:
                        data.images || updatedMessages[messageIndex].images,
                      audios:
                        data.audios || updatedMessages[messageIndex].audios,
                      videos:
                        data.videos || updatedMessages[messageIndex].videos,
                    };
                    return updatedMessages;
                  });
                }

                // Handle metadata and callbackMetadata updates
                if (
                  (data.metadata || data.callbackMetadata) &&
                  data.message_id
                ) {
                  // Log agent_processing_status for debugging
                  const parsedMetadata =
                    typeof data.metadata === "string"
                      ? safeJsonParse(data.metadata)
                      : data.metadata;
                  if (parsedMetadata?.agent_processing_status) {
                    console.log(
                      "[SSE] agent_processing_status update:",
                      parsedMetadata.agent_processing_status,
                      {
                        message_id: data.message_id,
                        status: data.status,
                        fullMetadata: parsedMetadata,
                      },
                    );
                    // Track processing status for cleanup phase detection
                    setCurrentProcessingStatus(
                      parsedMetadata.agent_processing_status,
                    );
                    currentProcessingStatusRef.current =
                      parsedMetadata.agent_processing_status;
                  }

                  // Bind this message_id as the streaming message. The backend
                  // only emits chat_retrieved (no chat_created) for existing
                  // chats, so without this streamingMessageID/assistantMessageId
                  // can stay null when the message was first created by the
                  // AppSync subscription, leaving cancel/complete unable to
                  // finalise it.
                  assistantMessageId = data.message_id;
                  setStreamingMessageID(data.message_id);
                  setWaitingForFirstResponse(false);

                  // Ensure message exists before updating metadata
                  // SSE events may arrive before onCreate subscription
                  setMessages((previousMessages) => {
                    const messageExists = previousMessages.find(
                      (m) => m._id === data.message_id,
                    );

                    if (!messageExists && data.status === "inProgress") {
                      // Message doesn't exist yet - create it from SSE event
                      console.log(
                        "[SSE] Creating message from SSE event:",
                        data.message_id,
                      );

                      return generateUniqueMessages([
                        ...previousMessages,
                        {
                          _id: data.message_id,
                          text: null,
                          createdAt:
                            Math.floor(new Date().getTime() / 1000) + 1,
                          _lastChangedAt: Date.now(),
                          user: {
                            id: null,
                            name: "assistant",
                            parentMessageId: optimisticUserMessageId,
                            agentName: parsedMetadata?.agentID
                              ? getAgent(parsedMetadata.agentID)?.label
                              : primaryAgent?.label,
                          },
                          loading: true,
                          metadata: parsedMetadata,
                          // Include files/media if present in inProgress event
                          files: data.files || [],
                          images: data.images || [],
                          audios: data.audios || [],
                          videos: data.videos || [],
                        },
                      ]);
                    }

                    // Message exists - update via normal flow
                    return previousMessages;
                  });

                  // Now update metadata (message should exist)
                  updateMessageMetadataSSE(
                    data.message_id,
                    data.metadata,
                    data.callbackMetadata,
                  );
                }
                // Handle fulfilled status - text streaming is complete, but metadata updates can still come
                if (data.status === "fulfilled" && data.type === "message") {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log("[SSE] fulfilled message received:", {
                      message_id: data.message_id,
                      metadata: data.metadata,
                      callbackMetadata: data.callbackMetadata,
                      files: data.files,
                    });
                  // Text streaming done - user can send another message
                  setIsAIStreaming(false);
                  setAllowStop(false);
                  setStreamingMessageID(null);
                  // Update message to remove cursor (text is finalized)
                  if (data.message_id || assistantMessageId) {
                    const msgId = data.message_id || assistantMessageId;
                    setMessages((prevMessages) => {
                      const messageIndex = prevMessages.findIndex(
                        (m) => m._id === msgId,
                      );
                      if (messageIndex === -1) return prevMessages;

                      // Skip updates for sealed messages (status === "continued")
                      if (prevMessages[messageIndex].status === "continued") {
                        return prevMessages;
                      }

                      const updatedMessages = [...prevMessages];
                      const currentText =
                        updatedMessages[messageIndex].text || "";
                      updatedMessages[messageIndex] = {
                        ...updatedMessages[messageIndex],
                        text: removeSpecificUnicodeAtEnd(currentText),
                        loading: false,
                        metadata: data.metadata
                          ? mergePlannerMetadata(
                              updatedMessages[messageIndex].metadata,
                              typeof data.metadata === "string"
                                ? safeJsonParse(data.metadata)
                                : data.metadata,
                            )
                          : updatedMessages[messageIndex].metadata,
                        callbackMetadata: data.callbackMetadata
                          ? typeof data.callbackMetadata === "string"
                            ? safeJsonParse(data.callbackMetadata, updatedMessages[messageIndex].callbackMetadata)
                            : data.callbackMetadata
                          : updatedMessages[messageIndex].callbackMetadata,
                        // Also update files/media if present in fulfilled event
                        files:
                          data.files || updatedMessages[messageIndex].files,
                        images:
                          data.images || updatedMessages[messageIndex].images,
                        audios:
                          data.audios || updatedMessages[messageIndex].audios,
                        videos:
                          data.videos || updatedMessages[messageIndex].videos,
                      };
                      return updatedMessages;
                    });
                  }
                  // Update chat state - text streaming is complete
                  chatsHelper.updateChat(chatIdRef.current, [
                    { property: "isAIReplying", value: false },
                    { property: "activeMessageForAI", value: null },
                  ]);
                }

                // Handle post_task_completed - all metadata is finalized
                // Uses message_id to update the correct message even if user started a new conversation
                if (
                  (data.type === "post_task_completed" ||
                    data.type === "post_task_generating_artifacts" ||
                    data.type === "post_task_sending_messages_over_channels") &&
                  data.message_id
                ) {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log("[SSE] post_task_completed received:", {
                      message_id: data.message_id,
                      metadata: data.metadata,
                      callbackMetadata: data.callbackMetadata,
                    });
                  // Update final metadata/callbackMetadata for the specific message
                  setMessages((prevMessages) => {
                    const messageIndex = prevMessages.findIndex(
                      (m) => m._id === data.message_id,
                    );
                    if (messageIndex === -1) return prevMessages;

                    const updatedMessages = [...prevMessages];
                    updatedMessages[messageIndex] = {
                      ...updatedMessages[messageIndex],
                      metadata: data.metadata
                        ? mergePlannerMetadata(
                            updatedMessages[messageIndex].metadata,
                            typeof data.metadata === "string"
                              ? safeJsonParse(data.metadata)
                              : data.metadata,
                          )
                        : updatedMessages[messageIndex].metadata,
                      callbackMetadata: data.callbackMetadata
                        ? typeof data.callbackMetadata === "string"
                          ? safeJsonParse(data.callbackMetadata, updatedMessages[messageIndex].callbackMetadata)
                          : data.callbackMetadata
                        : updatedMessages[messageIndex].callbackMetadata,
                      // Also update files/media if present
                      files: data.files || updatedMessages[messageIndex].files,
                      images:
                        data.images || updatedMessages[messageIndex].images,
                      audios:
                        data.audios || updatedMessages[messageIndex].audios,
                      videos:
                        data.videos || updatedMessages[messageIndex].videos,
                    };
                    return updatedMessages;
                  });
                }

                // Handle awaiting_user_input status - agent paused waiting for user response
                if (
                  data.status === "awaiting_user_input" ||
                  (data.metadata &&
                    (typeof data.metadata === "string"
                      ? safeJsonParse(data.metadata)
                      : data.metadata
                    )?.awaiting_user_response === true)
                ) {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log("[SSE] awaiting_user_input detected:", {
                      message_id: data.message_id,
                      status: data.status,
                      metadata: data.metadata,
                    });
                  setIsAwaitingUserInput(true);
                  setIsAIStreaming(false);
                  setWaitingForFirstResponse(false);
                  setWaitingForFirstChunk(false);
                  setAllowStop(false);
                }

                // Handle user_feedback_processed - update pending message status to fulfilled
                if (
                  data.type === "user_feedback_processed" &&
                  data.processed_feedback_id
                ) {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log("[SSE] user_feedback_processed received:", {
                      message_id: data.message_id,
                      processed_feedback_id: data.processed_feedback_id,
                      status: data.status,
                    });
                  setMessages((prevMessages) => {
                    const messageIndex = prevMessages.findIndex(
                      (m) => m._id === data.processed_feedback_id,
                    );
                    if (messageIndex === -1) return prevMessages;

                    const updatedMessages = [...prevMessages];
                    updatedMessages[messageIndex] = {
                      ...updatedMessages[messageIndex],
                      status: "ingested",
                    };
                    return updatedMessages;
                  });
                }

                // Handle tool_request event - backend agent needs local folder operation
                if (
                  data.type === "tool_request" &&
                  data.request_id &&
                  data.tool_name
                ) {
                  // Always log tool requests for troubleshooting (not dev-only)
                  console.log("[SSE Chatter] tool_request event received:", {
                    request_id: data.request_id,
                    tool_name: data.tool_name,
                    arguments: data.arguments,
                    boundFolderPath: boundFolderPathRef.current,
                    chatId: chatIdRef.current,
                    timestamp: new Date().toISOString(),
                  });
                  // Execute the tool request asynchronously (don't block SSE)
                  const wsToken = getActiveWorkspace()?.workspaceToken;
                  const wsId = getActiveWorkspace()?.id;
                  console.log(
                    "[SSE Chatter] Dispatching to handleToolRequest:",
                    {
                      hasWsToken: !!wsToken,
                      wsId,
                      hasBoundFolder: !!boundFolderPathRef.current,
                    },
                  );
                  handleToolRequest(
                    data,
                    boundFolderPathRef.current,
                    wsToken,
                    wsId,
                  );
                }
              },
              onComplete: async (finalText) => {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log("[SSE] onComplete:", {
                    finalTextLength: finalText?.length,
                    assistantMessageId,
                  });
                setIsAIStreaming(false);
                setAllowStop(false);
                setWaitingForFirstResponse(false);
                setWaitingForFirstChunk(false);
                // Clear processing status tracking as stream is complete
                setCurrentProcessingStatus(null);
                currentProcessingStatusRef.current = null;
                // Update message in state with final text (without cursor)
                if (assistantMessageId) {
                  setMessages((prevMessages) => {
                    const messageIndex = prevMessages.findIndex(
                      (m) => m._id === assistantMessageId,
                    );
                    if (messageIndex === -1) return prevMessages;

                    // Skip updates for sealed messages (status === "continued")
                    if (prevMessages[messageIndex].status === "continued") {
                      return prevMessages;
                    }

                    const updatedMessages = [...prevMessages];
                    updatedMessages[messageIndex] = {
                      ...updatedMessages[messageIndex],
                      text: removeSpecificUnicodeAtEnd(finalText),
                      loading: false,
                    };
                    return updatedMessages;
                  });
                }
                // Update chat state
                await chatsHelper.updateChat(chatIdRef.current, [
                  { property: "isAIReplying", value: false },
                  { property: "activeMessageForAI", value: null },
                ]);
              },
              onError: async (error) => {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log("[SSE] onError:", error);
                console.error("SSE stream error:", error);
                setIsAIStreaming(false);
                setAllowStop(false);
                setWaitingForFirstResponse(false);
                setWaitingForFirstChunk(false);
                // Handle quota errors with specific message, fallback to generic error
                if (!handleQuotaError(error)) {
                  AppToasty.show(t("chats.chatMessageError"), {
                    placement: "bottom",
                    type: "danger",
                  });
                }
                await chatsHelper.updateChat(chatIdRef.current, [
                  { property: "isAIReplying", value: false },
                  { property: "activeMessageForAI", value: null },
                ]);
              },
            },
          );
        } else {
          // Planner mode - use SSE streaming approach
          // Create user message and assistant placeholder via GraphQL first
          const userCreationTime = Math.floor(new Date().getTime() / 1000);
          const [userMessageResult, assistantMessageResult] = await Promise.all(
            [
              messagesHelper.createChatMessage({
                chatID: chatIdRef.current,
                workspaceID: getActiveWorkspace()?.id,
                text: message,
                role: user?.username,
                userID: user?.id,
                isAI: false,
                creationTime: userCreationTime,
                images: imageKeys ? imageKeys : null,
                audios: audioKeys ? audioKeys : null,
                videos: videoKey ? [videoKey] : null,
                files: fileKeys ? fileKeys : null,
              }),
              messagesHelper.createChatMessage({
                chatID: chatIdRef.current,
                workspaceID: getActiveWorkspace()?.id,
                text: "**...**",
                role: "assistant",
                isAI: true,
                creationTime: userCreationTime + 1,
                agentID: primaryAgent?.id,
              }),
            ],
          );

          const userMessageId = userMessageResult?.data?.createChatMessage?.id;
          const assistantMessageId =
            assistantMessageResult?.data?.createChatMessage?.id;

          // Add both messages to UI with real IDs from GraphQL
          setMessages((previousMessages) =>
            generateUniqueMessages([
              ...previousMessages,
              {
                _id: userMessageId,
                text: message,
                createdAt: userCreationTime,
                _lastChangedAt: Date.now(),
                user: { id: user?.id, name: user?.username, agentName: null },
                loading: false,
                images: imageKeys ? imageKeys : null,
                audios: audioKeys ? audioKeys : null,
                videos: videoKey ? [videoKey] : null,
                files: fileKeys ? fileKeys : null,
              },
              {
                _id: assistantMessageId,
                text: null,
                createdAt: userCreationTime + 1,
                _lastChangedAt: Date.now(),
                user: {
                  id: null,
                  name: "assistant",
                  parentMessageId: userMessageId,
                  agentName: primaryAgent?.label,
                },
                loading: true,
              },
            ]),
          );

          setStreamingMessageID(assistantMessageId);
          streamingMessageIDRef.current = assistantMessageId;
          // Note: isSSEStreaming from useAgentStream() is the source of truth for streaming state
          // No need to set setAllowStop or setIsAIStreaming - they are derived from isSSEStreaming
          isSendingEventRef.current = false;
          setWaitingForFirstResponse(true);
          setWaitingForFirstChunk(false);

          startSSEStream(
            {
              chatid: chatIdRef.current,
              workspaceid: getActiveWorkspace()?.id,
              token: getActiveWorkspace()?.workspaceToken,
              messages: [
                {
                  text: message,
                  role: "user",
                  userID: user?.id,
                  images: imageKeys ? imageKeys : null,
                  audios: audioKeys ? audioKeys : null,
                  videos: videoKey ? [videoKey] : null,
                  files: fileKeys ? fileKeys : null,
                },
              ],
              agentid: primaryAgent?.id || homeMode?.agentId,
              baseImage: selectedImageRawKey,
              canvasSelection: canvasSelection
                ? JSON.stringify(canvasSelection)
                : null,
              methodName: "planner",
              nextMessageId: assistantMessageId,
              // Include bound folder path for local folder access tool
              boundFolderPath: boundFolderPathRef.current,
            },
            {
              // No onToken handler - planner fetches messages on step completion for parallel execution
              onMessage: (data) => {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log("[SSE Planner] onMessage:", data);

                // Handle chat_created event
                if (data.type === "chat_created") {
                  setWaitingForFirstResponse(false);
                  setWaitingForFirstChunk(true);
                  // Update chat state
                  chatsHelper.updateChat(chatIdRef.current, [
                    { property: "isAIReplying", value: true },
                    {
                      property: "activeMessageForAI",
                      value: assistantMessageId,
                    },
                    { property: "forcePlannerStop", value: false },
                  ]);
                }

                // Handle plan_update events (planner-specific)
                if (data.type === "plan_update") {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log("[SSE Planner] plan_update:", {
                      planning_status: data.planning_status,
                      active_step: data.active_step,
                      total_steps: data.total_steps,
                    });
                  setPlanningExecution(data.planning_status);
                  if (data.active_step !== undefined) {
                    setActiveStep(data.active_step);
                  }
                  if (data.total_steps !== undefined) {
                    setTotalSteps(data.total_steps);
                  }
                  // Note: allowStop is now derived from isSSEStreaming - no need to set it manually
                }

                // Handle step_execution events (planner-specific)
                if (data.type === "step_execution") {
                  envConfig?.REACT_APP_ENV == "dev" &&
                    console.log("[SSE Planner] step_execution:", {
                      status: data.status,
                      step_id: data.step_id,
                      agent_id: data.agent_id,
                      artifact: data.artifact,
                      error: data.error,
                    });
                  // Update active step on started or artifact_generated
                  if (
                    (data.status === "started" ||
                      data.status === "artifact_generated") &&
                    data.step_id !== undefined
                  ) {
                    setActiveStep(data.step_id);
                    // Track multiple active steps for parallel execution visualization
                    setActiveSteps((prev) => {
                      const newSet = new Set(prev);
                      newSet.add(data.step_id);
                      return newSet;
                    });
                  }
                  // Handle step completion - fetch latest messages for parallel execution
                  if (
                    (data.status === "completed" ||
                      data.status === "artifact_generated") &&
                    data.step_id !== undefined
                  ) {
                    envConfig?.REACT_APP_ENV == "dev" &&
                      console.log(
                        "[SSE Planner] Step completed, fetching messages:",
                        data.step_id,
                        data.agent_id,
                      );
                    // Remove from active steps when completed
                    if (data.status === "completed") {
                      setActiveSteps((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(data.step_id);
                        return newSet;
                      });
                    }
                    // Fetch latest messages - this replaces GraphQL subscription streaming
                    // for parallel execution where multiple agents work simultaneously
                    fetchLatestMessages(chatIdRef.current, data.step_id);
                  }
                  if (data.error) {
                    AppToasty.show(data.error, {
                      placement: "bottom",
                      type: "danger",
                    });
                  }
                }

                // Handle tool_request event - backend agent needs local folder operation (planner)
                if (
                  data.type === "tool_request" &&
                  data.request_id &&
                  data.tool_name
                ) {
                  // Always log tool requests for troubleshooting (not dev-only)
                  console.log("[SSE Planner] tool_request event received:", {
                    request_id: data.request_id,
                    tool_name: data.tool_name,
                    arguments: data.arguments,
                    boundFolderPath: boundFolderPathRef.current,
                    chatId: chatIdRef.current,
                    timestamp: new Date().toISOString(),
                  });
                  // Execute the tool request asynchronously (don't block SSE)
                  const wsToken = getActiveWorkspace()?.workspaceToken;
                  const wsId = getActiveWorkspace()?.id;
                  console.log(
                    "[SSE Planner] Dispatching to handleToolRequest:",
                    {
                      hasWsToken: !!wsToken,
                      wsId,
                      hasBoundFolder: !!boundFolderPathRef.current,
                    },
                  );
                  handleToolRequest(
                    data,
                    boundFolderPathRef.current,
                    wsToken,
                    wsId,
                  );
                }
              },
              onError: async (error) => {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log("[SSE Planner] onError:", error);
                console.error("SSE Planner stream error:", error);
                // Note: isSSEStreaming will automatically become false when stream ends
                // Only need to reset UI-specific state
                setWaitingForFirstResponse(false);
                setWaitingForFirstChunk(false);
                // Handle quota errors with specific message, fallback to generic error
                if (!handleQuotaError(error)) {
                  AppToasty.show(t("chats.chatMessageError"), {
                    placement: "bottom",
                    type: "danger",
                  });
                }
                // Update chat state in DB to reflect stream ended
                await chatsHelper.updateChat(chatIdRef.current, [
                  { property: "isAIReplying", value: false },
                  { property: "activeMessageForAI", value: null },
                  { property: "forcePlannerStop", value: false },
                ]);
              },
              onComplete: async () => {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log("[SSE Planner] onComplete: Stream finished");
                // Fetch final messages when stream completes
                // This ensures we have all messages from parallel execution
                fetchLatestMessages(chatIdRef.current, -1); // -1 indicates final fetch
                setWaitingForFirstResponse(false);
                setWaitingForFirstChunk(false);
                // Clear processing status tracking as stream is complete
                setCurrentProcessingStatus(null);
                currentProcessingStatusRef.current = null;
                // Reset parallel steps tracking when stream completes
                setActiveSteps(new Set());
                // Update chat state in DB to reflect stream ended
                await chatsHelper.updateChat(chatIdRef.current, [
                  { property: "isAIReplying", value: false },
                  { property: "activeMessageForAI", value: null },
                  { property: "forcePlannerStop", value: false },
                ]);
              },
            },
          );
        }
      }
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" && console.error(error);
      try {
        // Note: isSSEStreaming will automatically become false if stream fails
        // Just update chat state in DB
        await chatsHelper.updateChat(chatIdRef.current, [
          {
            property: "isAIReplying",
            value: false,
          },
          {
            property: "activeMessageForAI",
            value: null,
          },
          {
            property: "forcePlannerStop",
            value: false,
          },
        ]);
      } catch (error) {
        envConfig?.REACT_APP_ENV == "dev" && console.error(error);
      }

      AppToasty.show(t("chats.chatMessageAdditionError"), {
        placement: "bottom",
        type: "danger",
      });
    } finally {
      // remove the loading message by checking the loading property
      // setMessages((previousMessages) =>
      //   previousMessages.filter((m) => !m.loading)
      // );
      // Clear canvas selection and all file attachment states after sending
      // so subsequent messages do not re-carry previous attachments
      setCanvasSelection(null);
      setImageUrl(null);
      setImageUrls(null);
      setImageKeys(null);
      setImageKey(null);
      setAudioUrl(null);
      setAudioUrls(null);
      setAudioKeys(null);
      setAudioKey(null);
      setFileUrl(null);
      setFileUrls(null);
      setFileKeys(null);
      setFileKey(null);
      setVideoUrl(null);
      setVideoKey(null);
      setSelectedImageKey(null);
      setSelectedImageRawKey(null);
      setAudiosText("");
      setVideosText("");
    }
  };
  const onSelectedTopicsChange = useCallback(
    (selectedItems) => {
      if (typeof selectedItems === "string") {
        selectedItems = JSON.parse(selectedItems);
      }
      if (!isLoading) setSelectedTopics(selectedItems);
    },
    [setSelectedTopics, isLoading],
  );
  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadChats();
    }
  }, [getActiveWorkspace()?.id]);
  useEffect(() => {
    window.onmessage = (e) => {
      if (e?.data?.tf_event_type == "form_submit" && "data" in e.data) {
        alert(JSON.stringify(e.data));
      }
    };
  }, [chatId]);
  useEffect(() => {
    // make sure chats are unique by id
    let uniqueChats = [];
    if (chats) {
      uniqueChats = chats.filter(
        (u, index, self) => index === self.findIndex((t) => t?.id === u?.id),
      );
    }
    if (homeMode?.isHomeMode && homeMode?.agentId) {
      // In home mode, filter to only show chats for the home agent and current user
      uniqueChats = uniqueChats.filter(
        (chat) => {
          const matchesAgent = homeMode.agentId === 'tf_main'
            ? chat?.primaryRole?.startsWith("tf_main")
            : chat?.primaryRole === homeMode.agentId;
          return matchesAgent && (!homeMode.filterByUser || chat?.createdBy === user?.id);
        },
      );
    } else {
      // In agents mode, exclude tf_main chats
      uniqueChats = uniqueChats.filter(
        (chat) => !chat?.primaryRole?.startsWith("tf_main"),
      );
    }
    setCoreTreeViewData(groupChatsByProperty(uniqueChats, "createdAt"));
  }, [
    JSON.stringify(
      chats?.map((u) => {
        return {
          id: u?.id,
          createdAt: u?.createdAt,
          primaryRole: u?.primaryRole,
          visibility: u?.visibility,
          name: u?.name,
          description: u?.description,
        };
      }),
    ),
    JSON.stringify(
      state?.agents
        ?.filter((u) => u?.agentType == "Chatbot")
        ?.map((u) => ({
          id: u?.id,
          agentCategoryType: u?.agentCategoryType,
          label: u?.label,
        })),
    ),
    JSON.stringify(state?.accessCredentials?.[0]?.favoriteAgents),
    homeMode?.isHomeMode,
    homeMode?.agentId,
    user?.id,
    JSON.stringify(
      (getWorkspaceEntities("topic") || [])
        ?.filter((e) => !e?._deleted)
        ?.map((e) => ({
          id: e?.id,
          label: e?.label,
          emoji: e?.emoji,
          backgroundColor: e?.backgroundColor,
        })),
    ),
  ]);
  const onDrop = async ({
    newTreeData,
    dragSourceId,
    dropTargetId,
    rest,
  }) => {};
  const handleShowSourcesDetails = (sources) => {
    setSelectedSources(sources);
    setShowSourcesModal(true);
  };

  /**
   * Fork a conversation from a specific assistant message
   * Creates a new chat with all messages up to and including the forking point
   * @param {string} messageId - The ID of the assistant message to fork from
   */
  const handleForkConversation = async (messageId) => {
    if (!messageId || !chatId) {
      AppToasty.show(t("toasts.forkFailed"), {
        placement: "bottom",
        type: "danger",
      });
      return;
    }

    // Find the message to fork from
    const forkingMessage = messages.find((m) => m._id === messageId);
    if (!forkingMessage) {
      AppToasty.show(t("chats.forkMessageNotFound") || "Message not found", {
        placement: "bottom",
        type: "danger",
      });
      return;
    }

    // Only allow forking from assistant messages
    if (
      forkingMessage.user?.name !== "assistant" &&
      forkingMessage.role !== "assistant"
    ) {
      AppToasty.show(
        t("chats.forkOnlyFromAssistant") ||
          "Can only fork from assistant messages",
        {
          placement: "bottom",
          type: "warning",
        },
      );
      return;
    }

    try {
      setIsLoading(true);

      // Get all messages up to and including the forking point
      const forkingMessageIndex = messages.findIndex(
        (m) => m._id === messageId,
      );
      const messagesToCopy = messages.slice(0, forkingMessageIndex + 1);

      if (messagesToCopy.length === 0) {
        AppToasty.show(t("chats.noMessagesToCopy") || "No messages to copy", {
          placement: "bottom",
          type: "warning",
        });
        setIsLoading(false);
        return;
      }

      // Create a new chat with the same properties as the original
      const originalChatName = chatObject?.name || "Chat";
      const forkedChatName = `${originalChatName} (Fork)`;
      const currentTime = Math.floor(new Date().getTime() / 1000);

      const newChatResult = await chatsHelper.createChat({
        workspaceID: getActiveWorkspace()?.id,
        allowMultipleRoles: chatObject?.allowMultipleRoles || false,
        createdBy: user?.id,
        visibility: chatObject?.visibility || "private",
        primaryRole: chatObject?.primaryRole,
        plannerAgent: chatObject?.plannerAgent,
        name: forkedChatName,
        description: chatObject?.description
          ? `Forked from: ${originalChatName}`
          : `Forked from: ${originalChatName}`,
        creationTime: currentTime,
        topics: chatObject?.topics ? JSON.stringify(chatObject.topics) : null,
      });

      const newChatId = newChatResult?.data?.createChat?.id;

      if (!newChatId) {
        throw new Error("Failed to create forked chat");
      }

      // Copy all messages up to the forking point
      const messageCreationPromises = messagesToCopy.map((msg, index) => {
        const isAssistant =
          msg.user?.name === "assistant" || msg.role === "assistant";

        // Build message payload, only including non-null values
        // DynamoDB indexes don't accept null values for indexed fields like humanFeedback
        const messagePayload = {
          chatID: newChatId,
          workspaceID: getActiveWorkspace()?.id,
          text: msg.text,
          role: isAssistant
            ? "assistant"
            : msg.user?.name || msg.role || "user",
          isAI: isAssistant,
          creationTime: currentTime + index, // Preserve order with incremental timestamps
        };

        // Only add optional fields if they have values
        if (!isAssistant && (msg.user?.id || user?.id)) {
          messagePayload.userID = msg.user?.id || user?.id;
        }
        if (msg.agentID || (isAssistant && primaryAgent?.id)) {
          messagePayload.agentID = msg.agentID || primaryAgent?.id;
        }
        if (msg.images?.length > 0) {
          messagePayload.images = msg.images;
        }
        if (msg.audios?.length > 0) {
          messagePayload.audios = msg.audios;
        }
        if (msg.videos?.length > 0) {
          messagePayload.videos = msg.videos;
        }
        if (msg.files?.length > 0) {
          messagePayload.files = msg.files;
        }
        if (msg.metadata) {
          messagePayload.metadata = JSON.stringify(msg.metadata);
        }
        // Don't copy humanFeedback fields - start fresh (omit them entirely)

        return messagesHelper.createChatMessage(messagePayload);
      });

      await Promise.all(messageCreationPromises);

      // Refresh chats list
      loadChats(false, true);

      // Show success message
      AppToasty.show(t("chats.toasts.forked"), {
        placement: "bottom",
        type: "success",
      });

      // Navigate to the new forked chat
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/${_routeBase}/${newChatId}`,
      );
      setIsCollapsed(true);
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.error("Error forking conversation:", error);
      AppToasty.show(t("chats.toasts.forkFailed"), {
        placement: "bottom",
        type: "danger",
      });
    } finally {
      setIsLoading(false);
    }
  };
  function groupChatsByProperty(chats, property = "primaryRole") {
    const RECENT_CHATS_LIMIT = 10;
    const favoriteAgents = state?.accessCredentials?.[0]?.favoriteAgents || [];

    const now = dayjs();
    const startOfThisWeek = now.startOf("week");
    const startOfLastWeek = startOfThisWeek.subtract(1, "week");
    const startOfThisMonth = now.startOf("month");
    const startOfLastMonth = startOfThisMonth.subtract(1, "month");

    const getTimePeriod = (chatDate) => {
      const date = dayjs(chatDate);
      if (date.isAfter(startOfThisWeek)) return "this_week";
      if (date.isAfter(startOfLastWeek)) return "last_week";
      if (date.isAfter(startOfThisMonth)) return "this_month";
      if (date.isAfter(startOfLastMonth)) return "last_month";
      return "older";
    };

    const TIME_PERIOD_CONFIG = {
      this_week: {
        id: "this_week",
        name: t("chats.timePeriods.thisWeek"),
        order: 1,
      },
      last_week: {
        id: "last_week",
        name: t("chats.timePeriods.lastWeek"),
        order: 2,
      },
      this_month: {
        id: "this_month",
        name: t("chats.timePeriods.thisMonth"),
        order: 3,
      },
      last_month: {
        id: "last_month",
        name: t("chats.timePeriods.lastMonth"),
        order: 4,
      },
      older: { id: "older", name: t("chats.timePeriods.older"), order: 5 },
    };

    const PERIOD_ORDER = [
      "this_week",
      "last_week",
      "this_month",
      "last_month",
      "older",
    ];

    // Home mode: first 10 chats flat, then grouped by time period
    if (homeMode?.isHomeMode) {
      const sortedChats = chats
        .filter((chat) => chat != null)
        .sort(
          (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
        );

      const recentChats = sortedChats.slice(0, RECENT_CHATS_LIMIT);
      const olderChats = sortedChats.slice(RECENT_CHATS_LIMIT);

      const result = recentChats.map((chat) => ({ ...chat, is_recent: true }));

      if (olderChats.length > 0) {
        const timeGroups = {};
        olderChats.forEach((chat) => {
          const period = getTimePeriod(chat.createdAt);
          if (!timeGroups[period]) timeGroups[period] = [];
          timeGroups[period].push(chat);
        });

        PERIOD_ORDER.filter((period) => timeGroups[period]?.length > 0).forEach(
          (period) => {
            result.push({
              id: period,
              name: TIME_PERIOD_CONFIG[period].name,
              is_time_group: true,
              droppable: true,
              children: timeGroups[period],
            });
          },
        );
      }

      return result;
    }

    const chatsByAgent = {};
    chats
      .filter((chat) => chat != null)
      .forEach((chat) => {
        const agentId = chat["primaryRole"];
        if (!chatsByAgent[agentId]) chatsByAgent[agentId] = [];
        chatsByAgent[agentId].push(chat);
      });

    Object.keys(chatsByAgent).forEach((agentId) => {
      chatsByAgent[agentId].sort((a, b) => {
        const dateA = dayjs(a.createdAt);
        const dateB = dayjs(b.createdAt);
        return dateB.valueOf() - dateA.valueOf();
      });
    });

    const groupedChats = {};

    const topicEntities = (getWorkspaceEntities("topic") || []).filter(
      (e) => !e?._deleted,
    );
    const topicById = {};
    topicEntities.forEach((t) => {
      topicById[t.id] = t;
    });

    state?.agents
      ?.filter((u) => u?.agentType == "Chatbot")
      ?.forEach((agent) => {
        const agentChats = chatsByAgent[agent.id] || [];
        const recentChats = agentChats.slice(0, RECENT_CHATS_LIMIT);
        const remainingChats = agentChats.slice(RECENT_CHATS_LIMIT);

        const groupTopic = agent?.agentCategoryType
          ? topicById[agent.agentCategoryType]
          : null;

        groupedChats[agent.id] = {
          id: agent.id,
          name: agent.label,
          description: agent?.description,
          is_agent: true,
          is_favorite: favoriteAgents?.includes(agent.id),
          agentType: agent?.agentType,
          icon: agent?.icon,
          color: agent?.color,
          darkColor: agent?.darkColor,
          mode: agent?.mode,
          droppable: true,
          agentCategoryType: agent?.agentCategoryType || null,
          groupLabel: groupTopic?.label || null,
          groupEmoji: groupTopic?.emoji || null,
          groupColor: groupTopic?.backgroundColor || null,
          children: recentChats.map((chat) => ({ ...chat, is_recent: true })),
        };

        if (remainingChats.length > 0) {
          const lastRecentPeriod =
            recentChats.length > 0
              ? getTimePeriod(recentChats[recentChats.length - 1].createdAt)
              : null;

          const lastRecentPeriodIndex = lastRecentPeriod
            ? PERIOD_ORDER.indexOf(lastRecentPeriod)
            : -1;

          const timeGroups = {};
          remainingChats.forEach((chat) => {
            const period = getTimePeriod(chat.createdAt);
            const periodIndex = PERIOD_ORDER.indexOf(period);
            if (periodIndex > lastRecentPeriodIndex) {
              if (!timeGroups[period]) timeGroups[period] = [];
              timeGroups[period].push(chat);
            }
          });

          Object.entries(timeGroups)
            .sort(
              (a, b) =>
                TIME_PERIOD_CONFIG[a[0]].order - TIME_PERIOD_CONFIG[b[0]].order,
            )
            .forEach(([periodKey, periodChats]) => {
              const config = TIME_PERIOD_CONFIG[periodKey];
              groupedChats[agent.id].children.push({
                id: `${agent.id}_${config.id}`,
                name: config.name,
                droppable: true,
                is_time_group: true,
                children: periodChats,
              });
            });
        }
      });

    const chatsWithDeletedAgents = chats.filter((chat) => {
      const agent = state?.agents?.find((a) => a.id === chat?.["primaryRole"]);
      return (
        chat != null && !agent && !chat?.["primaryRole"]?.startsWith("tf_main")
      );
    });

    if (chatsWithDeletedAgents.length > 0) {
      chatsWithDeletedAgents.sort((a, b) => {
        const dateA = dayjs(a.createdAt);
        const dateB = dayjs(b.createdAt);
        return dateB.valueOf() - dateA.valueOf();
      });

      const recentDeletedChats = chatsWithDeletedAgents.slice(
        0,
        RECENT_CHATS_LIMIT,
      );
      const remainingDeletedChats = chatsWithDeletedAgents.slice(
        RECENT_CHATS_LIMIT,
      );

      groupedChats["deleted_agents_id"] = {
        id: "deleted_agents_id",
        name: t("chats.agentDeleted"),
        description: t("chats.descriptionNotAvailable"),
        is_agent: true,
        is_favorite: false,
        is_agent_available: false,
        droppable: true,
        children: recentDeletedChats.map((chat) => ({
          ...chat,
          is_recent: true,
        })),
      };

      if (remainingDeletedChats.length > 0) {
        const lastRecentPeriod =
          recentDeletedChats.length > 0
            ? getTimePeriod(
                recentDeletedChats[recentDeletedChats.length - 1].createdAt,
              )
            : null;

        const lastRecentPeriodIndex = lastRecentPeriod
          ? PERIOD_ORDER.indexOf(lastRecentPeriod)
          : -1;

        const timeGroups = {};
        remainingDeletedChats.forEach((chat) => {
          const period = getTimePeriod(chat.createdAt);
          const periodIndex = PERIOD_ORDER.indexOf(period);
          if (periodIndex > lastRecentPeriodIndex) {
            if (!timeGroups[period]) timeGroups[period] = [];
            timeGroups[period].push(chat);
          }
        });

        Object.entries(timeGroups)
          .sort(
            (a, b) =>
              TIME_PERIOD_CONFIG[a[0]].order - TIME_PERIOD_CONFIG[b[0]].order,
          )
          .forEach(([periodKey, periodChats]) => {
            const config = TIME_PERIOD_CONFIG[periodKey];
            groupedChats["deleted_agents_id"].children.push({
              id: `deleted_agents_id_${config.id}`,
              name: config.name,
              droppable: true,
              is_time_group: true,
              children: periodChats,
            });
          });
      }
    }

    const allAgentNodes = Object.values(groupedChats);

    const deletedNodes = allAgentNodes.filter(
      (n) => n.id === "deleted_agents_id",
    );
    const nonDeletedNodes = allAgentNodes.filter(
      (n) => n.id !== "deleted_agents_id",
    );

    const favoriteAgentNodes = nonDeletedNodes
      .filter((n) => n.is_favorite)
      .sort((a, b) =>
        (a?.name || "").toLowerCase().localeCompare((b?.name || "").toLowerCase()),
      );

    const nonFavoriteAgents = nonDeletedNodes.filter((n) => !n.is_favorite);

    const groupedNonFavorite = nonFavoriteAgents.filter(
      (n) => n.agentCategoryType && n.groupLabel,
    );
    const ungroupedNonFavorite = nonFavoriteAgents.filter(
      (n) => !(n.agentCategoryType && n.groupLabel),
    );

    const groupMap = {};
    groupedNonFavorite.forEach((agent) => {
      const gid = agent.agentCategoryType;
      if (!groupMap[gid]) {
        const topic = topicById[gid];
        groupMap[gid] = {
          id: `group_${gid}`,
          name: topic?.label || agent.groupLabel,
          is_agent_group: true,
          droppable: true,
          groupEmoji: topic?.emoji || agent.groupEmoji,
          groupColor: topic?.backgroundColor || agent.groupColor,
          children: [],
        };
      }
      groupMap[gid].children.push(agent);
    });

    const groupNodes = Object.values(groupMap).sort((a, b) =>
      (a?.name || "").toLowerCase().localeCompare((b?.name || "").toLowerCase()),
    );

    ungroupedNonFavorite.sort((a, b) =>
      (a?.name || "").toLowerCase().localeCompare((b?.name || "").toLowerCase()),
    );

    const sortedGroupedChats = [
      ...favoriteAgentNodes,
      ...groupNodes,
      ...ungroupedNonFavorite,
      ...deletedNodes,
    ];

    return sortedGroupedChats;
  }
  const isAIReplyingOtherUsers =
    state?.chats?.find((chat) => chat?.id === chatId)?.isAIReplying &&
    !isAIStreaming;
  // Resolve agent function IDs to full function objects for ChatWebInput
  const resolvedAgentFunctions = React.useMemo(() => {
    if (!primaryAgent?.agentFunctions?.length || !workspaceFunctions?.length) {
      return [];
    }
    return primaryAgent.agentFunctions
      .map((functionId) => {
        const fn = workspaceFunctions.find((f) => f?.id === functionId);
        if (!fn) return null;
        return {
          ...fn,
          label: fn?.name,
        };
      })
      .filter(Boolean);
  }, [primaryAgent?.agentFunctions, workspaceFunctions]);

  const handleToggleFavoriteAgent = async (agentId, isFavorite) => {
    try {
      const currentAccessCredentials = state?.accessCredentials?.[0];
      if (!currentAccessCredentials) return;

      const currentFavorites = currentAccessCredentials?.favoriteAgents || [];
      let updatedFavorites;

      if (isFavorite) {
        updatedFavorites = [...currentFavorites, agentId];
      } else {
        updatedFavorites = currentFavorites.filter((id) => id !== agentId);
      }

      await usersAPI.updateUserFavoriteAgents(
        currentAccessCredentials.id,
        currentAccessCredentials._version,
        updatedFavorites,
      );

      dispatch(
        "SET_ACCESS_CREDENTIALS",
        state?.accessCredentials?.map((cred) =>
          cred.id === currentAccessCredentials.id
            ? { ...cred, favoriteAgents: updatedFavorites }
            : cred,
        ),
      );

      AppToasty.show(
        isFavorite ? t("ui.addedToFavorites") : t("ui.removedFromFavorites"),
        {
          placement: "bottom",
          type: "success",
        },
      );
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.error("Error updating favorite agents:", error);
      AppToasty.show(t("chats.toasts.favoriteUpdateFailed"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };

  const handleUpdateRecentFolders = async (folderPath) => {
    try {
      const currentAccessCredentials = state?.accessCredentials?.[0];
      if (!currentAccessCredentials) return;

      const currentRecentFolders =
        currentAccessCredentials?.recentLocalFolders || [];

      const filteredFolders = currentRecentFolders.filter(
        (path) => path && path !== folderPath,
      );

      const updatedRecentFolders = [folderPath, ...filteredFolders].slice(
        0,
        10,
      );

      await usersAPI.updateRecentLocalFolders(
        currentAccessCredentials.id,
        currentAccessCredentials._version,
        updatedRecentFolders,
      );

      dispatch(
        "SET_ACCESS_CREDENTIALS",
        state?.accessCredentials?.map((cred) =>
          cred.id === currentAccessCredentials.id
            ? {
                ...cred,
                recentLocalFolders: updatedRecentFolders,
                _version: (currentAccessCredentials._version || 0) + 1,
              }
            : cred,
        ),
      );
    } catch (error) {
      envConfig?.REACT_APP_ENV == "dev" &&
        console.error("Error updating recent folders:", error);
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LiveVoiceProvider
        workspaceId={getActiveWorkspace()?.id}
        apiKey={getActiveWorkspace()?.workspaceToken}
        chatId={chatId}
        agentId={chatObject?.primaryRole || homeMode?.agentId}
        liveSessionId={liveSessionId}
        onVoiceSessionEnded={handleVoiceSessionEnded}
        voiceMinSilenceDuration={primaryAgent?.voiceMinSilenceDuration || 1.0}
        voiceMinEndpointingDelay={primaryAgent?.voiceMinEndpointingDelay || 0.8}
      >
      <ChatsScreenView
        builderMode={builderMode}
        isHomeMode={homeMode?.isHomeMode}
        homeAgentId={homeMode?.agentId}
        liveSessionId={liveSessionId}
        onVoiceSessionEnded={handleVoiceSessionEnded}
        cursor={cursor}
        onSelectionQuantumEditor={onSelectionQuantumEditor}
        showWidgetChatsHistory={showWidgetChatsHistory}
        handleClickOnWidgetChatsHistory={handleClickOnWidgetChatsHistory}
        setShowWidgetChatsHistory={setShowWidgetChatsHistory}
        isMoreToLoad={!state?.chatsLoaded}
        errorWithSubscription={errorWithSubscription}
        handleClickOnNode={handleClickOnNode}
        SaveChat={createChat}
        selectedNode={selectedNode}
        deleteNode={removeNode}
        showModal={showModal}
        setShowModal={setShowModal}
        setDialogActionOnTree={setDialogActionOnTree}
        treeViewActionContext={treeViewActionContext}
        isTreeDataLoading={!isChatHistoryLoaded || deletionInProgress}
        selectedTopics={selectedTopics}
        onSelectedTopicsChange={onSelectedTopicsChange}
        topics={generateAdaptedTopics(topics)}
        onDrop={onDrop}
        isSocketConnected={isSocketConnected}
        _coreTreeViewData={_coreTreeViewData}
        selectedNodeToDelete={selectedNodeToDelete}
        creationOfNewChatInProgress={creationOfNewChatInProgress}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        setSelectedNode={setSelectedNode}
        onMove={onMove}
        chatId={chatId}
        documentId={documentId}
        isLoading={isLoading}
        primaryAgent={primaryAgent}
        resolvedAgentFunctions={resolvedAgentFunctions}
        agents={state?.agents || []}
        onAgentSelected={handleAgentSelected}
        onSendWithAgent={handleSendWithAgent}
        pendingUserMessages={messages?.filter(
          (m) =>
            m?.status === "pending" &&
            m?.user?.name !== "assistant" &&
            m?.role !== "assistant",
        )}
        messages={messages
          ?.filter(
            (m) =>
              // Always hide pending user messages (queued while agent is responding)
              !(
                m?.status === "pending" &&
                m?.user?.name !== "assistant" &&
                m?.role !== "assistant"
              ) &&
              // Hide fulfilled/ingested non-assistant messages for agenticRAG agents
              !(
                (m?.user?.name != "assistant" || m?.role === "user") &&
                (m?.status === "fulfilled" || m?.status === "ingested") &&
                primaryAgent?.agenticRAG == true
              ),
          )
          ?.map((u, index, filteredMessages) => {
            const isLast = index === filteredMessages.length - 1;
            const text = u?.text || " ";
            return isLast
              ? { ...u, text: text }
              : {
                  ...u,
                  text: removeSpecificUnicodeAtEnd(text),
                };
          })}
        setMessages={setMessages}
        hasAccessToChat={hasAccessToChat}
        onSendEvent={onSendEvent}
        chatObject={chatObject}
        isAIStreaming={
          // For planner mode, use isSSEStreaming as single source of truth
          primaryAgent?.mode === "planner"
            ? isSSEStreaming && chatObject?.forcePlannerStop !== true
            : isAIStreaming && chatObject?.forcePlannerStop !== true
        }
        isAIReplyingOtherUsers={isAIReplyingOtherUsers}
        waitingForFirstResponse={waitingForFirstResponse}
        waitingForFirstChunk={waitingForFirstChunk}
        isAwaitingUserInput={isAwaitingUserInput}
        onCancelGeneration={onCancelGeneration}
        inputText={inputText}
        setInputText={setInputText}
        onLoadEarlier={onLoadEarlier}
        allMessagesLoaded={stateMessages.length >= loadedMessages}
        subscriptionObj={subscriptionObj}
        streamingMessageID={streamingMessageID}
        documents={state?.embeddedMrcDocuments}
        fixedDocumentId={fixedDocumentId}
        setTreeViewActionContext={setTreeViewActionContext}
        S3downloadUrlRequest={S3downloadUrlRequest}
        handleFeedback={handleFeedback}
        workspaceid={getActiveWorkspace()?.id}
        onImageSelected={onImageSelected}
        selectedImageKey={selectedImageKey}
        handleCopy={handleCopy}
        handleShowSourcesDetails={handleShowSourcesDetails}
        showSourcesModal={showSourcesModal}
        setShowSourcesModal={setShowSourcesModal}
        selectedSources={selectedSources}
        allowStop={
          // For planner mode, use isSSEStreaming as single source of truth
          primaryAgent?.mode === "planner" ? isSSEStreaming : allowStop
        }
        setIsChatHistoryLoaded={setIsChatHistoryLoaded}
        isLastMessageEmpty={lastMessageFromAssistant?.text == ""}
        canInteractWithChat={canInteractWithChat}
        handleParentActionClick={handleParentActionClick}
        onToggleFavorite={handleToggleFavoriteAgent}
        onImageUploaded={onImageUploaded}
        imageUrl={imageUrl}
        setImageUrl={setImageUrl}
        onAudioUploaded={onAudioUploaded}
        audioUrl={audioUrl}
        setAudioUrl={setAudioUrl}
        onVideoUploaded={onVideoUploaded}
        videoUrl={videoUrl}
        setVideoUrl={setVideoUrl}
        onFileUploaded={onFileUploaded}
        fileUrl={fileUrl}
        setFileUrl={setFileUrl}
        onFilesUploaded={onFilesUploaded}
        fileUrls={fileUrls}
        setFileUrls={setFileUrls}
        imageUrls={imageUrls}
        audioUrls={audioUrls}
        forceAgentsName={getAgent(chatObject?.plannerAgent)?.mode == "planner"}
        activeStep={activeStep}
        activeSteps={activeSteps}
        isFeedbackOnPlanAllowed={
          isSSEStreaming &&
          activeStep > 0 &&
          totalSteps > 0 &&
          primaryAgent?.mode === "planner"
        }
        isAgenticRAGAcceptingFeedback={
          (isSSEStreaming || isAIStreaming) &&
          (primaryAgent?.agenticRAG === true ||
            primaryAgent?.useInterleavedReasoning === true)
        }
        planningExecution={
          primaryAgent?.mode === "planner" && planningExecution
        }
        totalSteps={totalSteps}
        isSpeechGettingProcessed={isSpeechGettingProcessed}
        updateTextForSpeech={updateTextForSpeech}
        stopAudio={() => {
          stopAudio();
          setIsPlaying(false);
        }}
        isPlaying={isPlaying}
        listenToAudio={listenToAudio}
        lastUpdateMessageTime={lastUpdateMessageTime}
        agentHosting={agentHosting}
        planArtifacts={planArtifacts}
        stepsStatusFromArtifacts={stepsStatusFromArtifacts}
        updatePlanApprovalState={updatePlanApprovalState}
        // EditorWrapper props for canvas collaboration
        value={value}
        editor={editor}
        isInactive={isInactive}
        updateAPIError={updateAPIError}
        updateAPIInProgress={updateAPIInProgress}
        createAPIInProgress={createAPIInProgress}
        saveDocument={saveDocument}
        handleSocketConnectionChange={handleSocketConnectionChange}
        handleEditorError={handleEditorError}
        setValue={setValue}
        setEditor={setEditor}
        editorIsLoading={editorIsLoading}
        isReadyForRTC={isReadyForRTC}
        isDocumentValid={isDocumentValid}
        wordsCount={wordsCount}
        charactersCount={charactersCount}
        isSaved={isSaved}
        setLastCancellation={setLastCancellation}
        setIsSaveRequested={setIsSaveRequested}
        handleOnRecorderClick={handleOnRecorderClick}
        handleCanvasCreation={handleCanvasCreation}
        planMode={planMode}
        planFile={planFile}
        planStatus={planStatus}
        handlePlanModeToggle={handlePlanModeToggle}
        handleShowPlan={handleShowPlan}
        handleForkConversation={handleForkConversation}
        // Model selection props
        onBaseModelChange={handleBaseModelChange}
        isModelChangeInProgress={isModelChangeInProgress}
        canChangeModel={isUserAIEngineer() || isUserContentAdmin()}
        // Tool toggle props
        onToolToggle={handleToolToggle}
        toolToggleInProgress={toolToggleInProgress}
        canToggleTools={isUserAIEngineer() || isUserContentAdmin()}
        onHandOffToggle={handleHandOffToggle}
        // Folder binding props
        onFolderBound={handleFolderBound}
        onFolderUnbound={handleFolderUnbound}
        recentFolders={
          state?.accessCredentials?.[0]?.recentLocalFolders?.filter(Boolean) ||
          []
        }
        onUpdateRecentFolders={handleUpdateRecentFolders}
        // Mark for training
        onMarkForTraining={handleMarkForTraining}
        // UoI usage visibility
        canViewUoIUsage={isUserAIEngineer() || isUserContentAdmin()}
      />
      <PlanViewerModal
        visible={showPlanModal}
        setVisible={setShowPlanModal}
        planContent={planContent}
        isLoading={planContentLoading}
      />
      </LiveVoiceProvider>
    </Suspense>
  );
};

export default Chats;
